import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { io } from 'socket.io-client';
import styles from './Octadesk.module.css';
import api from '../../services/api';
import {
  fetchOctadeskMetricas,
  fetchOctadeskSalas,
  fetchOctadeskGrupos,
  fetchOctadeskStatus,
  fetchOctadeskMensagens,
  fetchOctadeskTriagem,
  fetchOctadeskResumoDiario,
  fetchOctadeskGerarResumo,
  fetchOctadeskSentimento,
  fetchOctadeskMencoesProduto,
  fetchOctadeskMetricasTempo,
  fetchOctadeskReclamacoesGrupos,
  fetchOctadeskReclamacoesGrupoSalas,
  fetchOctadeskReclamacoesDetalhe,
  fetchOctadeskReclamacoesPorSub,
  fetchOctadeskLeadsQuentes,
  fetchOctadeskProdutosComReclamacao
} from '../../services/api';

// ---- Helpers de Triagem ----
const CATEGORIA_CONFIG = {
  DUVIDA_PRODUTO: { label: 'Produto',    cor: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  PRECO:          { label: 'Preço',      cor: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  PEDIDO:         { label: 'Pedido',     cor: '#22c55e', bg: 'rgba(34,197,94,0.12)'  },
  RECLAMACAO:     { label: 'Reclamação', cor: '#ef4444', bg: 'rgba(239,68,68,0.12)'  },
  SAUDACAO:       { label: 'Saudação',   cor: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
  OUTRO:          { label: 'Outro',      cor: '#78716c', bg: 'rgba(120,113,108,0.12)'},
};
const URGENCIA_ICONE = { ALTA: '🔴', MEDIA: '🟡', BAIXA: '🟢' };

// ---- Motivos de reclamação (taxonomia nova, snake_case) ----
// Grupo "problemas de SITE" usa a mesma família de azul para saltar aos olhos
// junto (site_erro + site_indisponivel + login_cadastro + falha_pagamento = maior dor).
const SUBCAT_CFG = {
  SITE_ERRO:           { label: 'Erro no site',                    cor: '#3b82f6', emoji: '🖥️' },
  LOGIN_CADASTRO:      { label: 'Não consegue logar/cadastrar',     cor: '#60a5fa', emoji: '🔑' },
  SITE_INDISPONIVEL:   { label: 'Site fora do ar',                  cor: '#1d4ed8', emoji: '🌐' },
  FALHA_PAGAMENTO:     { label: 'Pagamento não conclui',            cor: '#2563eb', emoji: '💳' },

  DEFEITO:             { label: 'Produto com defeito',              cor: '#ef4444', emoji: '⚠️' },
  PRODUTO_ERRADO:      { label: 'Produto errado',                   cor: '#f97316', emoji: '📦' },
  MAU_ATENDIMENTO:     { label: 'Atendimento ruim',                 cor: '#a855f7', emoji: '👥' },
  QUALIDADE:           { label: 'Qualidade abaixo do esperado',     cor: '#f59e0b', emoji: '⭐' },
  COBRANCA_INDEVIDA:   { label: 'Cobrança indevida',                cor: '#ec4899', emoji: '💸' },
  ATRASO_ENTREGA:      { label: 'Entrega atrasada',                 cor: '#06b6d4', emoji: '🚚' },
  OUTRO:               { label: 'Outro',                            cor: '#78716c', emoji: '❓' },

  // Vazamento técnico do classificador (grava o método, não o motivo) — visual neutro de propósito.
  REGRA_PALAVRA_CHAVE: { label: 'Não classificado',                 cor: '#475569', emoji: '⏳' },
  NAO_CLASSIFICADO:    { label: 'Não classificado',                 cor: '#475569', emoji: '⏳' },

  // Resíduos da taxonomia antiga, preservados de propósito (dado histórico).
  PRODUTO:             { label: 'Produto (categoria antiga)',       cor: '#fca5a5', emoji: '🗄️' },
  ENTREGA:             { label: 'Entrega (categoria antiga)',       cor: '#67e8f9', emoji: '🗄️' },
  PRECO:               { label: 'Preço (categoria antiga)',         cor: '#d8b4fe', emoji: '🗄️' },
  ATENDIMENTO:         { label: 'Atendimento (categoria antiga)',   cor: '#93c5fd', emoji: '🗄️' },
  DEVOLUCAO:           { label: 'Devolução (categoria antiga)',     cor: '#a5f3fc', emoji: '🗄️' },
};
function getSubcatCfg(subCategoria) {
  const key = String(subCategoria || '').toUpperCase();
  return SUBCAT_CFG[key] || SUBCAT_CFG.OUTRO;
}

function TriagemBadge({ triagem }) {
  if (!triagem) return null;
  const cfg = CATEGORIA_CONFIG[triagem.categoria] || CATEGORIA_CONFIG.OUTRO;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 11px', borderRadius: 999,
      /* 0.72 -> 0.86rem e borda mais forte: no tema claro o badge quase sumia
         (pedido do dono 2026-08-24 — aumentar e destacar a leitura da lista) */
      fontSize: '0.86rem', fontWeight: 700,
      color: cfg.cor, background: cfg.bg,
      border: `1px solid ${cfg.cor}55`,
      whiteSpace: 'nowrap'
    }}>
      {URGENCIA_ICONE[triagem.urgencia]} {cfg.label}
    </span>
  );
}

// ---- Ícones ----
const IconWhatsApp = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
const IconAlert  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IconUser   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconPhone  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;
const IconClose  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconRefresh= () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>;
const IconSearch = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconHeadset= () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><path d="M3 18v-6a9 9 0 0118 0v6M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z"/></svg>;

// ---- Utilitários ----
// Usa campos normalizados que o backend já extraiu (_nomeCliente, _telefone, _ultimaMsg)
function extrairTelefone(sala) {
  return sala._telefone || null;
}

function extrairNome(sala) {
  return sala._nomeCliente || sala.contact?.displayName || sala.contact?.name || 'Sem nome';
}

function extrairTextoMensagem(sala) {
  if (sala._ultimaMsg) return sala._ultimaMsg;
  const raw = sala.lastMessage?.comment || sala.lastMessage?.text || sala.lastMessage?.body || '';
  return raw.replace(/<[^>]*>/g, '').trim() || '—';
}

// Detecta se o remetente é atendente da Gringão (vs cliente)
// Critérios: nome contém palavras-chave de staff + lista de atendentes conhecidos.
// Necessário porque o sync atual não diferencia origem='USER' (cliente) de
// origem='AGENT' (atendente humano) — ambos vêm como USER.
const ATENDENTES_KNOWN = new Set([
  'marcela atendimento', 'stefany ribeiro', 'bárbara atendimento',
  'priscilla neves', 'tuani -  vendedora', 'tuani - vendedora',
  'loja física centro', 'carol - vendedora', 'talita ferreira fajardo',
  'loja gringão - caxias', 'paulo cendon - gringão', 'atendimento site',
  'luan menezes', 'valquíria dione jesus dos santos',
  'miriam dos santos pereira', 'marcelle coutinho dos santos'
]);
function isAtendente(nome) {
  if (!nome) return false;
  const n = nome.toLowerCase().replace(/\s+/g, ' ').trim();
  if (ATENDENTES_KNOWN.has(n)) return true;
  return /atendimento|vendedor[a]?\b|gringão|gringao|\bloja\s/i.test(nome);
}

function formatarHorario(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  const diffMin = Math.floor((Date.now() - d) / 60000);
  if (diffMin < 1)  return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  const h = Math.floor(diffMin / 60);
  if (h < 24)       return `${h}h atrás`;
  return `${Math.floor(h / 24)}d atrás`;
}

function formatarHora(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatarDataSeparador(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  const mesmoDia = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();
  if (mesmoDia(d, hoje))  return 'Hoje';
  if (mesmoDia(d, ontem)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function diaDaMsg(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// ---- MetricCard ----
/* MetricCard removido em 2026-08-25: ficou sem uso quando os indicadores migraram para
   CardDestaque/CardComSatelites/CardMotivo (design do mockup). O CSS .metricCard/.card*
   permanece porque .metricSkeleton ainda e usado pelos cards novos. */

/**
 * Paginação dos painéis de drill-down. Client-side de propósito: o painel já carregou
 * a lista inteira (até 500) numa chamada só, então paginar aqui é instantâneo e não
 * gera round-trip a cada clique.
 */
function PaginacaoPainel({ total, pagina, porPagina, onPagina }) {
  const totalPaginas = Math.ceil(total / porPagina);
  if (totalPaginas <= 1) return null;
  const ini = (pagina - 1) * porPagina + 1;
  const fim = Math.min(pagina * porPagina, total);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(148,163,184,0.15)'
    }}>
      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
        {ini}–{fim} de {total}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => onPagina(pagina - 1)}
          disabled={pagina <= 1}
          style={{
            background: 'rgba(148,163,184,0.12)', border: 'none', borderRadius: 6,
            color: pagina <= 1 ? '#b6bec9' : '#334155', cursor: pagina <= 1 ? 'not-allowed' : 'pointer',
            padding: '5px 12px', fontSize: '0.8rem'
          }}
        >‹ Anterior</button>
        <span style={{ color: '#94a3b8', fontSize: '0.8rem', minWidth: 74, textAlign: 'center' }}>
          {pagina} / {totalPaginas}
        </span>
        <button
          onClick={() => onPagina(pagina + 1)}
          disabled={pagina >= totalPaginas}
          style={{
            background: 'rgba(148,163,184,0.12)', border: 'none', borderRadius: 6,
            color: pagina >= totalPaginas ? '#b6bec9' : '#334155',
            cursor: pagina >= totalPaginas ? 'not-allowed' : 'pointer',
            padding: '5px 12px', fontSize: '0.8rem'
          }}
        >Próxima ›</button>
      </div>
    </div>
  );
}

/**
 * Modal único dos cards (2026-08-24). Todo card abre este modal com o seu recorte.
 *
 * Substitui os 5 painéis inline: eles abriam como grid abaixo dos cards, empurravam a
 * página e competiam por espaço. Aqui a lista rola dentro da caixa e a paginação fica
 * fixa no rodapé.
 *
 * `tipo` decide o formato da linha, porque cada recorte carrega dados diferentes:
 *   salas | grupo | leads
 */
function ModalCard({ dados, pagina, porPagina, onPagina, onFechar, onAbrirConversa, triagens }) {
  // Filtro por sinal (2026-08-25, pedido do dono): clicar num mini-card do resumo
  // (Revenda/Atacado/...) filtra a lista abaixo para só aquele sinal; clicar de novo
  // limpa. Reseta quando o modal abre ou troca de conteúdo.
  const [filtroSinal, setFiltroSinal] = useState(null);
  useEffect(() => { setFiltroSinal(null); }, [dados]);
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', onEsc);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // trava o scroll do fundo
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = overflowAnterior;
    };
  }, [onFechar]);

  if (!dados) return null;
  const { titulo, emoji, cor, tipo, itens = [], carregando, subtitulo, resumo } = dados;
  // Cada item de resumo com `chave` vira filtro. O `campo` diz como filtrar:
  //   'sinal' (leads)          -> it[`sinal_<chave>`] e verdadeiro
  //   'sentimento'/'sub_categoria' -> igualdade simples
  const aplicaFiltro = (it) => {
    if (!filtroSinal) return true;
    if (filtroSinal.campo === 'sinal') return !!it[`sinal_${filtroSinal.chave}`];
    return it[filtroSinal.campo] === filtroSinal.chave;
  };
  const itensFiltrados = itens.filter(aplicaFiltro);
  const total = itensFiltrados.length;
  const visiveis = itensFiltrados.slice((pagina - 1) * porPagina, pagina * porPagina);

  const linha = (r, i) => {
    const nome = r._nomeCliente || r.nome_cliente || 'Sem nome';
    const fone = r._telefone || r.telefone;
    const naoLidas = r.unreadMessages || r.nao_lidas || 0;
    const tri = triagens?.[r.key] || null;
    let direita = null;
    let selos = [];
    let borda = cor;

    if (tipo === 'leads') {
      borda = '#22c55e';
      selos = (r.sinais || []).map(sn => (
        <span key={sn.chave} style={{
          fontSize: '0.68rem', fontWeight: 700, padding: '1px 8px', borderRadius: 999,
          color: sn.forte ? '#22c55e' : '#94a3b8',
          border: `1px solid ${sn.forte ? '#22c55e' : '#475569'}`,
          background: sn.forte ? 'rgba(34,197,94,0.10)' : 'transparent'
        }}>{sn.rotulo}</span>
      ));
      direita = <div style={{ color: '#94a3b8' }}>{r.dias_parado != null ? (r.dias_parado < 1 ? 'hoje' : `há ${Math.round(r.dias_parado)}d`) : ''}</div>;
    } else if (tipo === 'grupo') {
      const neg = r.sentimento === 'INSATISFEITO';
      const pos = r.sentimento === 'SATISFEITO';
      borda = neg ? '#ef4444' : pos ? '#22c55e' : cor;
      if (r.sub_categoria) {
        const sc = getSubcatCfg(r.sub_categoria);
        selos.push(<span key="sc" style={{ fontSize: '0.68rem', color: sc.cor, border: `1px solid ${sc.cor}44`, borderRadius: 999, padding: '1px 7px' }}>{sc.emoji} {sc.label}</span>);
      }
      if (neg) selos.push(<span key="sent" style={{ fontSize: '0.68rem', color: '#ef4444', fontWeight: 700 }}>😠 insatisfeito</span>);
      if (pos) selos.push(<span key="sent" style={{ fontSize: '0.68rem', color: '#22c55e', fontWeight: 700 }}>😊 elogio</span>);
    } else {
      if (naoLidas > 0) {
        borda = '#ef4444';
        selos.push(<span key="nl" style={{ fontSize: '0.68rem', fontWeight: 700, color: '#ef4444', border: '1px solid #ef4444', borderRadius: 999, padding: '1px 7px' }}>{naoLidas} não lida{naoLidas > 1 ? 's' : ''}</span>);
      }
      if (tri?.categoria) selos.push(<span key="cat" style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{tri.categoria}</span>);
    }

    return (
      <div
        key={r.key || i}
        onClick={() => onAbrirConversa(r)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.55rem 0.75rem', background: '#f7f8fa', borderRadius: 8,
          cursor: 'pointer', borderLeft: `3px solid ${borda}`
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ color: '#0f172a' }}>{nome}</strong>
            {fone && <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{fone}</span>}
            {selos}
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(r.resumo || r._ultimaMsg || r.ultima_mensagem || '—').slice(0, 130)}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#94a3b8', minWidth: 96 }}>
          {direita}
          {(r.agente || r.assigned?.name)
            ? <div>👤 {r.agente || r.assigned.name}</div>
            : <div style={{ color: '#ef4444' }}>sem agente</div>}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className={styles.modalCardOverlay} onClick={onFechar}>
      <div className={styles.modalCardBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalCardHeader}>
          <h3 className={styles.modalCardTitulo} style={{ color: cor }}>
            <span>{emoji}</span>
            {titulo}
            {!carregando && <span className={styles.modalCardSub}>{total} {total === 1 ? 'conversa' : 'conversas'}</span>}
          </h3>
          <button className={styles.modalCardFechar} onClick={onFechar} title="Fechar (Esc)">✕</button>
        </div>

        <div className={styles.modalCardCorpo}>
          {subtitulo && !carregando && (
            <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: 4 }}>{subtitulo}</div>
          )}
          {resumo?.length > 0 && !carregando && (
            <div className={styles.satelitesGrid} style={{ marginBottom: 10 }}>
              {resumo.map(x => {
                const ativo = filtroSinal?.chave === x.chave;
                return (
                  <div
                    key={x.rotulo}
                    className={styles.satelite}
                    onClick={x.chave ? () => { setFiltroSinal(ativo ? null : { campo: x.campo || 'sinal', chave: x.chave }); onPagina(1); } : undefined}
                    role={x.chave ? 'button' : undefined}
                    title={x.chave ? (ativo ? 'Clique para ver todos' : `Ver só ${x.rotulo}`) : undefined}
                    style={x.chave ? {
                      cursor: 'pointer',
                      borderColor: ativo ? cor : undefined,
                      background: ativo ? `color-mix(in srgb, ${cor} 8%, #fff)` : undefined,
                      boxShadow: ativo ? `0 0 0 1px ${cor}` : undefined
                    } : undefined}
                  >
                    <span className={styles.sateliteValor} style={ativo ? { color: cor } : undefined}>{x.valor.toLocaleString('pt-BR')}</span>
                    <span className={styles.sateliteRotulo}>{x.rotulo}{ativo ? ' ✕' : ''}</span>
                  </div>
                );
              })}
            </div>
          )}
          {filtroSinal && !carregando && (
            <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 6 }}>
              Mostrando só <strong>{resumo.find(x => x.chave === filtroSinal.chave)?.rotulo}</strong> ({total}) — clique no card de novo para limpar
            </div>
          )}
          {carregando
            ? <div className={styles.modalCardVazio}>Carregando…</div>
            : total === 0
              ? <div className={styles.modalCardVazio}>Nenhuma conversa neste recorte.</div>
              : visiveis.map(linha)}
        </div>

        {!carregando && total > porPagina && (
          <div className={styles.modalCardRodape}>
            <PaginacaoPainel total={total} pagina={pagina} porPagina={porPagina} onPagina={onPagina} />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ---- Exportação de conversas (.txt) ----
// Opções fixas do modal. Mesmas categorias/urgências dos chips da lista — se um dia
// a taxonomia mudar, muda nos dois lugares (a lista usa a sua própria cópia porque
// lá os chips são de seleção ÚNICA; aqui são múltipla).
const EXPORT_CATEGORIAS = [
  { v: 'DUVIDA_PRODUTO', label: '🔵 Dúvida' },
  { v: 'PRECO',          label: '🟣 Preço' },
  { v: 'PEDIDO',         label: '🟢 Pedido' },
  { v: 'RECLAMACAO',     label: '🔴 Reclamação' },
  { v: 'SAUDACAO',       label: '⚪ Saudação' },
  { v: 'OUTRO',          label: '🟤 Outro' },
];
const EXPORT_URGENCIAS = [
  { v: 'ALTA',  label: '🔴 Alta' },
  { v: 'MEDIA', label: '🟡 Média' },
  { v: 'BAIXA', label: '🟢 Baixa' },
];
// Sentinela combinada com o backend: "conversa sem agente atribuído". Não dá para
// usar string vazia — na querystring ela já significa "todos os agentes".
const EXPORT_SEM_AGENTE = '__sem_agente__';

// Cada item selecionado carrega o TIPO no próprio valor, no formato que a rota
// espera (ver /exportar-conversas): "a:<nome>" = agente atribuído da sala,
// "v:<nome>" = vendedora (participou como remetente). Sem isso o backend não teria
// como saber em qual coluna procurar — vendedora nunca aparece em salas.agente.
const valAgente   = (nome) => `a:${nome}`;
const valVendedor = (nome) => `v:${nome}`;
const nomeDoValor = (v) => (v === EXPORT_SEM_AGENTE ? 'Sem agente atribuído' : String(v).replace(/^[av]:/i, ''));
const ehVendedor  = (v) => /^v:/i.test(String(v));

const EXPORT_PADRAO_VAZIO = {
  status: 'todas', busca: '', categorias: [], urgencias: [], agentes: [],
  dataInicio: '', dataFim: '', falante: 'todos', disparos: true,
};

/** Filtros do modal → querystring da rota (mesmo objeto para prévia e download). */
function montarParamsExport(f) {
  const p = {};
  if (f.status === 'abertas')  p.status = 0;
  if (f.status === 'fechadas') p.status = 1;
  if (f.busca)              p.busca      = f.busca;
  if (f.categorias.length)  p.categorias = f.categorias.join(',');
  if (f.urgencias.length)   p.urgencias  = f.urgencias.join(',');
  // "|" e não vírgula: nome de agente pode ter vírgula (ver comentário na rota).
  if (f.agentes?.length)    p.agentes    = f.agentes.join('|');
  if (f.dataInicio)         p.dataInicio = f.dataInicio;
  if (f.dataFim)            p.dataFim    = f.dataFim;
  if (f.falante !== 'todos') p.falante   = f.falante;
  if (!f.disparos)          p.disparos   = 0;
  return p;
}

/**
 * Seletor de "quem atendeu" do modal de exportação — MÚLTIPLA escolha
 * (2026-09-09, pedido do dono: "precisamos que o usuário consiga escolher um ou
 * mais agentes... e dentro dos agentes mostrar também os vendedores").
 *
 * Era um <select> de uma opção só, alimentado pelo ranking de agentes (top 10 de
 * salas ABERTAS): dava para exportar um agente por vez e quem não tinha conversa
 * aberta agora sumia da lista. Agora a lista vem de /agentes-filtro (acervo
 * inteiro) e o painel abre INLINE — o corpo do modal tem overflow-y auto e
 * cortaria um dropdown posicionado absoluto.
 *
 * Duas seções porque são duas coisas diferentes no banco:
 *   • Atendimento  → agente ATRIBUÍDO à conversa (octadesk_salas.agente);
 *   • Vendedoras   → quem FALOU na conversa (remetente da mensagem). A sala segue
 *     atribuída ao atendimento, então filtrar por vendedora traz a conversa em que
 *     ela apareceu — é o que o dono quer ver.
 */
function SeletorAgentes({ valor = [], onChange, lista, carregando, erro }) {
  const [aberto, setAberto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const caixaRef = useRef(null);
  const buscaRef = useRef(null);

  useEffect(() => { if (aberto) buscaRef.current?.focus(); else setFiltro(''); }, [aberto]);

  // Esc fecha SÓ o painel, não o modal inteiro: o modal escuta keydown no
  // document em bubbling, então aqui vai em CAPTURE (roda antes) + stopPropagation.
  useEffect(() => {
    if (!aberto) return undefined;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setAberto(false);
    };
    // 'click' e NÃO 'mousedown': o painel é inline e empurra o resto do modal para
    // baixo. Fechando no mousedown, o conteúdo sobe ~200px antes do mouseup, o
    // mouseup cai em outro elemento e o navegador não dispara click nenhum no botão
    // que o usuário mirou — com o painel aberto, os chips "Só o cliente/Só a loja",
    // o checkbox de disparos e o "Baixar .txt" ignoravam o primeiro clique.
    // Em CAPTURE porque .modalCardBox faz stopPropagation() no onClick (para o
    // clique dentro do modal não fechá-lo): no bubbling este listener nunca seria
    // chamado. Na captura o clique ainda segue seu curso normal até o alvo.
    const onClicar = (e) => { if (!caixaRef.current?.contains(e.target)) setAberto(false); };
    document.addEventListener('keydown', onEsc, true);
    document.addEventListener('click', onClicar, true);
    return () => {
      document.removeEventListener('keydown', onEsc, true);
      document.removeEventListener('click', onClicar, true);
    };
  }, [aberto]);

  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const casa = (nome) => !filtro.trim() || norm(nome).includes(norm(filtro));

  const agentes    = (lista?.agentes    || []).filter((a) => casa(a.nome));
  const vendedores = (lista?.vendedores || []).filter((v) => casa(v.nome));
  const nada = !carregando && !agentes.length && !vendedores.length;

  const alterna = (v) => onChange(valor.includes(v) ? valor.filter((x) => x !== v) : [...valor, v]);

  // Função, não sub-componente: definir um componente aqui dentro remontaria os
  // checkboxes a cada tecla digitada na busca.
  const linha = (v, nome, total, dica) => (
    <label key={v} className={`${styles.expDropItem} ${valor.includes(v) ? styles.expDropItemAtivo : ''}`} title={dica}>
      <input type="checkbox" checked={valor.includes(v)} onChange={() => alterna(v)} />
      <span className={styles.expDropNome}>{nome}</span>
      {total != null && <span className={styles.expDropTotal}>{Number(total).toLocaleString('pt-BR')}</span>}
    </label>
  );

  const resumo = valor.length === 0 ? '👤 Todos os agentes'
    : valor.length === 1 ? `👤 ${nomeDoValor(valor[0])}`
    : `👤 ${valor.length} selecionados`;

  return (
    <div className={styles.expDrop} ref={caixaRef}>
      <div className={styles.expLinha}>
        <button
          type="button"
          className={`${styles.chipIA} ${valor.length ? styles.chipIAAtivo : ''}`}
          onClick={() => setAberto((a) => !a)}
          title="Escolher um ou mais agentes/vendedores"
        >
          {resumo} <span className={styles.expDropSeta}>{aberto ? '▲' : '▼'}</span>
        </button>
        {valor.length > 0 && (
          <button type="button" className={styles.chipIALimpar} onClick={() => onChange([])}>✕ limpar</button>
        )}
      </div>

      {/* Selecionados sempre à vista, mesmo com o painel fechado */}
      {valor.length > 0 && (
        <div className={styles.expLinha}>
          {valor.map((v) => (
            <button
              key={v} type="button" className={styles.expTagSel}
              onClick={() => alterna(v)} title="Remover deste filtro"
            >
              {ehVendedor(v) ? '🛍️' : '👤'} {nomeDoValor(v)} ✕
            </button>
          ))}
        </div>
      )}

      {aberto && (
        <div className={styles.expDropPainel}>
          <input
            ref={buscaRef} type="text" className={styles.expDropBusca}
            value={filtro} onChange={(e) => setFiltro(e.target.value)}
            placeholder="Procurar agente ou vendedora..."
          />
          {carregando && <div className={styles.expDropAviso}>⏳ Carregando a lista...</div>}
          {erro && <div className={styles.expDropAviso}>⚠️ {erro}</div>}

          {!!lista?.semAgente && casa('sem agente atribuido') && (
            <div className={styles.expDropGrupo}>
              {linha(EXPORT_SEM_AGENTE, '🚫 Sem agente atribuído', lista.semAgente, 'Conversas que ninguém assumiu')}
            </div>
          )}

          {agentes.length > 0 && (
            <div className={styles.expDropGrupo}>
              <span className={styles.expDropTitulo}>
                Atendimento <span className={styles.expLabelDica}>(agente da conversa)</span>
              </span>
              {agentes.map((a) => linha(valAgente(a.nome), a.nome, a.total,
                `${a.total} conversa(s)${a.abertas ? ` · ${a.abertas} aberta(s)` : ''}`))}
            </div>
          )}

          {vendedores.length > 0 && (
            <div className={styles.expDropGrupo}>
              <span className={styles.expDropTitulo}>
                Vendedoras <span className={styles.expLabelDica}>(quem falou na conversa)</span>
              </span>
              {vendedores.map((v) => linha(valVendedor(v.nome), v.nome, v.total,
                `Apareceu em ${v.total} conversa(s)`))}
            </div>
          )}

          {nada && <div className={styles.expDropAviso}>Nenhum nome com “{filtro}”.</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Modal de exportação (2026-09-08, pedido do dono: "quando for baixar as conversas
 * abrir um modal com todos os filtros").
 *
 * Antes o botão baixava direto com os filtros que estivessem na lista — para pegar
 * um recorte diferente era preciso mexer na tela inteira (e depois desfazer). Aqui
 * os filtros são do EXPORT: abrem com o que está na tela (é o que o usuário acabou
 * de olhar) e podem ser mudados sem tocar na lista.
 *
 * Dois filtros que a lista não tem, porque só fazem sentido num arquivo:
 *   - "quem falou" (só cliente / só loja) — corta MENSAGEM, não conversa;
 *   - "incluir disparos" — tira as mensagens de campanha (origem BULK) do meio.
 * A prévia (conversas/mensagens) chama a mesma rota com `contar=1` antes de gerar
 * o arquivo: 77 mil mensagens no acervo inteiro, melhor saber o tamanho antes.
 */
function ModalExportarConversas({ aberto, padrao, agentes = [], onFechar }) {
  const [f, setF] = useState(padrao || EXPORT_PADRAO_VAZIO);
  const [previa, setPrevia] = useState({ carregando: true, salas: 0, mensagens: 0, aproximado: false, erro: null });
  const [baixando, setBaixando] = useState(false);
  // Lista do seletor de agentes: acervo inteiro + vendedoras (o ranking que a tela
  // já tem em memória só traz o top 10 de abertas, e serve só de plano B aqui).
  const [listaAgentes, setListaAgentes] = useState({ dados: null, carregando: false, erro: null });

  // Abrir = recomeçar dos filtros da tela. Só no `aberto` (não em `padrao`): se a
  // lista atualizar sozinha pelo socket com o modal aberto, o que o usuário marcou
  // aqui não pode ser jogado fora no meio.
  useEffect(() => {
    if (aberto) { setF(padrao || EXPORT_PADRAO_VAZIO); setBaixando(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // Lista de agentes/vendedoras: uma vez por sessão do modal (o backend ainda
  // cacheia 5 min por cima). Enquanto não chega, cai no ranking que a tela passou.
  useEffect(() => {
    if (!aberto || listaAgentes.dados || listaAgentes.carregando) return;
    let vivo = true;
    setListaAgentes({ dados: null, carregando: true, erro: null });
    api.get('/api/octadesk/agentes-filtro')
      .then((r) => {
        if (!vivo) return;
        setListaAgentes({
          dados: {
            agentes:    r.data?.agentes    || [],
            vendedores: r.data?.vendedores || [],
            semAgente:  r.data?.semAgente  || 0,
          },
          carregando: false, erro: null,
        });
      })
      .catch(() => {
        if (!vivo) return;
        setListaAgentes({
          dados: { agentes, vendedores: [], semAgente: 0 },
          carregando: false,
          erro: 'não consegui carregar a lista completa — mostrando só os agentes com conversa aberta',
        });
      });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', onEsc);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = overflowAnterior;
    };
  }, [aberto, onFechar]);

  // Prévia com debounce: cada clique num chip mudaria a contagem, e a rota varre
  // as mensagens — 450 ms evita uma consulta por clique.
  useEffect(() => {
    if (!aberto) return undefined;
    let vivo = true;
    setPrevia((p) => ({ ...p, carregando: true, erro: null }));
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/api/octadesk/exportar-conversas', { params: { ...montarParamsExport(f), contar: 1 } });
        if (!vivo) return;
        setPrevia({
          carregando: false,
          salas: r.data?.salas || 0,
          mensagens: r.data?.mensagens || 0,
          aproximado: !!r.data?.aproximado,
          erro: null,
        });
      } catch (e) {
        if (vivo) setPrevia({ carregando: false, salas: 0, mensagens: 0, aproximado: false, erro: 'não consegui calcular' });
      }
    }, 450);
    return () => { vivo = false; clearTimeout(t); };
  }, [aberto, f]);

  if (!aberto) return null;

  const set = (campo, valor) => setF((atual) => ({ ...atual, [campo]: valor }));
  const alterna = (campo, v) => setF((atual) => ({
    ...atual,
    [campo]: atual[campo].includes(v) ? atual[campo].filter((x) => x !== v) : [...atual[campo], v],
  }));

  // Atalhos em horário LOCAL: toISOString() devolve UTC e, em UTC-3, empurra o dia
  // para frente depois das 21h (armadilha já paga no FaturamentoHeader).
  const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const hoje = new Date();
  const atalhos = [
    { label: 'Hoje',        ini: isoLocal(hoje), fim: isoLocal(hoje) },
    { label: 'Ontem',       ini: isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1)), fim: isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1)) },
    { label: 'Últimos 7 dias', ini: isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 6)), fim: isoLocal(hoje) },
    { label: 'Este mês',    ini: isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), fim: isoLocal(hoje) },
    { label: 'Mês passado', ini: isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)), fim: isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 0)) },
    { label: 'Tudo',        ini: '', fim: '' },
  ];

  const baixar = async () => {
    if (baixando) return;
    setBaixando(true);
    try {
      const r = await api.get('/api/octadesk/exportar-conversas', {
        params: montarParamsExport(f),
        responseType: 'blob',
      });
      const nome = (f.dataInicio || f.dataFim)
        ? `conversas_octadesk_${f.dataInicio || 'inicio'}_a_${f.dataFim || 'hoje'}.txt`
        : 'conversas_octadesk_completo.txt';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      onFechar();
    } catch (e) {
      console.error('[Octadesk] Falha ao exportar conversas:', e);
      alert('Não consegui gerar o arquivo de conversas. Tente de novo em alguns segundos.');
    } finally {
      setBaixando(false);
    }
  };

  const periodoTxt = (f.dataInicio || f.dataFim)
    ? `${f.dataInicio || 'início'} até ${f.dataFim || 'hoje'}`
    : 'acervo completo';
  const vazio = !previa.carregando && !previa.erro && previa.salas === 0;

  return createPortal(
    <div className={styles.modalCardOverlay} onClick={onFechar}>
      <div className={styles.modalCardBox} style={{ width: 'min(760px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalCardHeader}>
          <h3 className={styles.modalCardTitulo} style={{ color: '#0f172a' }}>
            <span>⬇️</span>
            Baixar conversas em .txt
            <span className={styles.modalCardSub}>{periodoTxt}</span>
          </h3>
          <button className={styles.modalCardFechar} onClick={onFechar} title="Fechar (Esc)">✕</button>
        </div>

        <div className={styles.modalCardCorpo}>
          {/* Período */}
          <div className={styles.expGrupo}>
            <span className={styles.expLabel}>Período das mensagens</span>
            <div className={styles.expLinha}>
              <input
                type="date" className={styles.resumoDatePicker} value={f.dataInicio}
                max={f.dataFim || isoLocal(hoje)}
                onChange={(e) => set('dataInicio', e.target.value)} aria-label="Período de"
              />
              <span className={styles.expSep}>–</span>
              <input
                type="date" className={styles.resumoDatePicker} value={f.dataFim}
                min={f.dataInicio || undefined} max={isoLocal(hoje)}
                onChange={(e) => set('dataFim', e.target.value)} aria-label="Período até"
              />
            </div>
            <div className={styles.expLinha}>
              {atalhos.map((a) => (
                <button
                  key={a.label}
                  className={`${styles.chipIA} ${f.dataInicio === a.ini && f.dataFim === a.fim ? styles.chipIAAtivo : ''}`}
                  onClick={() => setF((atual) => ({ ...atual, dataInicio: a.ini, dataFim: a.fim }))}
                >
                  {a.label}
                </button>
              ))}
            </div>
            <span className={styles.expDica}>
              O corte é pela data de CADA mensagem — conversa sem mensagem no período fica de fora.
            </span>
          </div>

          {/* Status */}
          <div className={styles.expGrupo}>
            <span className={styles.expLabel}>Status da conversa</span>
            <div className={styles.expLinha}>
              {[
                { v: 'todas',    label: 'Todas' },
                { v: 'abertas',  label: '🟢 Abertas' },
                { v: 'fechadas', label: '⚫ Fechadas' },
              ].map(({ v, label }) => (
                <button key={v}
                  className={`${styles.chipIA} ${f.status === v ? styles.chipIAAtivo : ''}`}
                  onClick={() => set('status', v)}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Triagem IA — categoria (múltipla) */}
          <div className={styles.expGrupo}>
            <span className={styles.expLabel}>
              Triagem IA — assunto
              <span className={styles.expLabelDica}>(pode marcar mais de um; nenhum = todos)</span>
            </span>
            <div className={styles.expLinha}>
              {EXPORT_CATEGORIAS.map(({ v, label }) => (
                <button key={v}
                  className={`${styles.chipIA} ${f.categorias.includes(v) ? styles.chipIAAtivo : ''}`}
                  onClick={() => alterna('categorias', v)}>
                  {label}
                </button>
              ))}
              {f.categorias.length > 0 && (
                <button className={styles.chipIALimpar} onClick={() => set('categorias', [])}>✕ limpar</button>
              )}
            </div>
          </div>

          {/* Urgência (múltipla) */}
          <div className={styles.expGrupo}>
            <span className={styles.expLabel}>
              Urgência
              <span className={styles.expLabelDica}>(pode marcar mais de uma)</span>
            </span>
            <div className={styles.expLinha}>
              {EXPORT_URGENCIAS.map(({ v, label }) => (
                <button key={v}
                  className={`${styles.chipIA} ${f.urgencias.includes(v) ? styles.chipIAAtivo : ''}`}
                  onClick={() => alterna('urgencias', v)}>
                  {label}
                </button>
              ))}
              {f.urgencias.length > 0 && (
                <button className={styles.chipIALimpar} onClick={() => set('urgencias', [])}>✕ limpar</button>
              )}
            </div>
            <span className={styles.expDica}>
              Assunto e urgência vêm da triagem da IA — conversa ainda não triada não entra quando algum deles está marcado.
            </span>
          </div>

          {/* Agente + busca */}
          <div className={styles.expGrupo}>
            <span className={styles.expLabel}>
              Agente e cliente
              <span className={styles.expLabelDica}>(pode marcar mais de um; nenhum = todos)</span>
            </span>
            <SeletorAgentes
              valor={f.agentes}
              onChange={(v) => set('agentes', v)}
              lista={listaAgentes.dados || { agentes, vendedores: [], semAgente: 0 }}
              carregando={listaAgentes.carregando}
              erro={listaAgentes.erro}
            />
            <div className={styles.expLinha}>
              <input
                type="text" className={styles.expInputBusca} value={f.busca}
                placeholder="Cliente ou telefone (opcional)"
                onChange={(e) => set('busca', e.target.value)}
              />
            </div>
            <span className={styles.expDica}>
              Agente = quem está com a conversa. Vendedora = quem falou dentro dela (a conversa
              continua atribuída ao atendimento).
            </span>
          </div>

          {/* Filtros só do arquivo */}
          <div className={styles.expGrupo}>
            <span className={styles.expLabel}>O que entra no arquivo</span>
            <div className={styles.expLinha}>
              {[
                { v: 'todos',   label: '💬 Cliente e loja' },
                { v: 'cliente', label: '🙋 Só o cliente' },
                { v: 'loja',    label: '🏪 Só a loja' },
              ].map(({ v, label }) => (
                <button key={v}
                  className={`${styles.chipIA} ${f.falante === v ? styles.chipIAAtivo : ''}`}
                  onClick={() => set('falante', v)}>
                  {label}
                </button>
              ))}
              <label className={styles.expCheck} title="Mensagens de campanha/disparo em massa (origem BULK)">
                <input type="checkbox" checked={f.disparos} onChange={(e) => set('disparos', e.target.checked)} />
                Incluir disparos em massa
              </label>
            </div>
          </div>

          {/* Prévia */}
          <div className={`${styles.expPrevia} ${vazio ? styles.expPreviaVazia : ''}`}>
            {previa.carregando ? '⏳ Calculando o tamanho...'
              : previa.erro ? `⚠️ ${previa.erro} — dá para baixar assim mesmo`
              : vazio ? '🔍 Nenhuma conversa com esses filtros — ajuste algo acima'
              : `📄 ${previa.aproximado ? 'até ' : ''}${previa.salas.toLocaleString('pt-BR')} conversa${previa.salas === 1 ? '' : 's'} · ${previa.mensagens.toLocaleString('pt-BR')} mensagem${previa.mensagens === 1 ? '' : 's'}`}
            {previa.aproximado && !previa.carregando && !vazio && (
              <span className={styles.expDica} style={{ marginLeft: 8 }}>
                (o filtro de quem falou é aplicado na hora de escrever; o total real sai no rodapé do arquivo)
              </span>
            )}
          </div>
        </div>

        <div className={styles.modalCardRodape} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className={styles.chipIALimpar} onClick={() => setF(EXPORT_PADRAO_VAZIO)}>✕ Limpar tudo</button>
          <button className={styles.chipIA} onClick={() => setF(padrao || EXPORT_PADRAO_VAZIO)}>↩︎ Filtros da tela</button>
          <span style={{ flex: 1 }} />
          <button className={styles.chipIA} onClick={onFechar}>Cancelar</button>
          <button
            className={styles.expBotaoBaixar}
            onClick={baixar}
            disabled={baixando || vazio}
            style={{ opacity: baixando || vazio ? 0.6 : 1, cursor: baixando ? 'wait' : vazio ? 'not-allowed' : 'pointer' }}
          >
            {baixando ? '⏳ Gerando...' : '⬇️ Baixar .txt'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Card de destaque com barra de proporção (usado em Reclamações Abertas). */
function CardDestaque({ titulo, icone, cor, valor, complemento, proporcao, legenda, carregando, onClick }) {
  return (
    <div
      className={styles.cardBlocoLg}
      style={{ '--accent': cor }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div className={styles.cardBlocoTopo}>
        <span className={styles.cardBlocoTitulo}>{titulo}</span>
        <span className={styles.cardBlocoIcone}>{icone}</span>
      </div>
      {carregando ? <span className={styles.metricSkeleton} /> : (
        <>
          <div className={styles.cardBlocoLinhaValor}>
            <span className={styles.cardBlocoValor}>{(valor ?? 0).toLocaleString('pt-BR')}</span>
            {complemento && <span className={styles.cardBlocoComplemento}>{complemento}</span>}
          </div>
          {proporcao != null && (
            <div className={styles.barraProporcao}>
              <div className={styles.barraProporcaoFill} style={{ width: `${Math.min(100, Math.max(2, proporcao))}%` }} />
            </div>
          )}
          {legenda && <span className={styles.cardBlocoLegenda}>{legenda}</span>}
        </>
      )}
    </div>
  );
}

/* CardComSatelites removido em 2026-08-25: a quebra por sinal dos leads saiu do card
   e passou a aparecer dentro do modal (prop `resumo` do ModalCard), a pedido do dono.
   As classes .satelite* do CSS continuam em uso — agora pelo modal. */

/** Card de motivo: número em destaque + quebra por sub-categoria (label ··· valor). */
function CardMotivo({ titulo, icone, cor, valor, itens = [], carregando, onClick }) {
  return (
    <div
      className={styles.cardMotivo}
      style={{ '--accent': cor }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div className={styles.cardBlocoTopo}>
        <span className={styles.cardBlocoTitulo}>{titulo}</span>
        <span className={styles.cardBlocoIcone}>{icone}</span>
      </div>
      {carregando ? <span className={styles.metricSkeleton} /> : (
        <>
          <span className={styles.cardMotivoValor}>{(valor ?? 0).toLocaleString('pt-BR')}</span>
          <div className={styles.cardMotivoLista}>
            {itens.length === 0
              ? <span className={styles.cardMotivoVazio}>sem ocorrências</span>
              : itens.map(it => (
                  <div key={it.rotulo} className={styles.cardMotivoItem}>
                    <span className={styles.cardMotivoItemRotulo}>{it.rotulo}</span>
                    <span className={styles.cardMotivoItemValor}>{it.valor.toLocaleString('pt-BR')}</span>
                  </div>
                ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`${styles.statusBadge} ${status === 0 ? styles.statusAberta : styles.statusFechada}`}>
      {status === 0 ? '● Aberta' : '● Fechada'}
    </span>
  );
}



// ---- Painel Lateral de Conversa ----
function PainelConversa({ sala, triagem, onFechar, filtroProduto }) {
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [nomeResolvido, setNomeResolvido] = useState('');
  const [limiteMsgs, setLimiteMsgs] = useState(60);
  const [carregandoMais, setCarregandoMais] = useState(false);
  // Edição + envio da resposta sugerida (bot human-in-the-loop)
  const msgRefProduto = useRef(null);

  // Reset do limite ao trocar de sala
  useEffect(() => {
    setLimiteMsgs(60);
  }, [sala?.key, sala?._id]);

  useEffect(() => {
    if (!sala) return;
    const ac = new AbortController();
    const eCargaInicial = limiteMsgs === 60;
    if (eCargaInicial) {
      setCarregando(true); setMensagens([]); setNomeResolvido('');
    } else {
      setCarregandoMais(true);
    }
    const roomKey = sala.key || sala._id;
    fetchOctadeskMensagens(roomKey, limiteMsgs, ac.signal)
      .then(r => {
        if (ac.signal.aborted) return;
        if (r.success) {
          const msgs = r.data || [];
          setMensagens(msgs);
          if (eCargaInicial && (!sala._nomeCliente || sala._nomeCliente === 'Sem nome')) {
            const primeiraUser = msgs.find(m => m.origem === 'USER' && m.remetente);
            if (primeiraUser) setNomeResolvido(primeiraUser.remetente);
          }
        }
      })
      .catch(err => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      })
      .finally(() => {
        if (ac.signal.aborted) return;
        setCarregando(false);
        setCarregandoMais(false);
      });
    return () => ac.abort();
  }, [sala?.key, sala?._id, limiteMsgs]);

  const carregarMaisAntigas = () => {
    if (carregandoMais) return;
    setLimiteMsgs(n => n + 60);
  };

  // Rolar até a mensagem que menciona o produto após carregar
  useEffect(() => {
    if (!filtroProduto || carregando || !msgRefProduto.current) return;
    setTimeout(() => {
      msgRefProduto.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }, [filtroProduto, carregando]);

  if (!sala) return null;
  const nome     = (sala._nomeCliente && sala._nomeCliente !== 'Sem nome') ? sala._nomeCliente : (nomeResolvido || 'Sem nome');
  const telefone = sala._telefone || null;
  const naoLidas = sala.unreadMessages || 0;
  const cfg      = triagem ? (CATEGORIA_CONFIG[triagem.categoria] || CATEGORIA_CONFIG.OUTRO) : null;

  return createPortal(
    <div className={styles.painelOverlay} onClick={onFechar}>
      <aside className={styles.painel} onClick={e => e.stopPropagation()}>
        <div className={styles.painelHeader}>
          <div className={styles.painelAvatar}>{nome.charAt(0).toUpperCase()}</div>
          <div className={styles.painelInfo}>
            <strong className={styles.painelNome}>{nome}</strong>
            {telefone && <a href={`tel:${telefone}`} className={styles.painelTelefone}><IconPhone /> {telefone}</a>}
            <div className={styles.painelMeta}>
              <StatusBadge status={sala.status} />
              {naoLidas > 0 && <span className={styles.badgeNaoLidas}>{naoLidas} não lidas</span>}
              {sala.assigned?.name && <span className={styles.agenteTag}>👤 {sala.assigned.name}</span>}
              {triagem && <TriagemBadge triagem={triagem} />}
              {triagem?.sub_categoria && (
                <span className={styles.subCatTag} title="Sub-categoria">
                  {triagem.sub_categoria.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            {triagem?.urgencia_critica ? (
              <div className={styles.alertaCritico}>
                🚨 <strong>URGÊNCIA CRÍTICA</strong> — atender já
                {Array.isArray(triagem.palavras_chave_alerta) && triagem.palavras_chave_alerta.length > 0 && (
                  <span className={styles.alertaCriticoPalavras}>
                    {' '}({triagem.palavras_chave_alerta.join(' · ')})
                  </span>
                )}
              </div>
            ) : (Array.isArray(triagem?.palavras_chave_alerta) && triagem.palavras_chave_alerta.length > 0) && (
              <div className={styles.alertaModerado}>
                ⚠️ {triagem.palavras_chave_alerta.join(' · ')}
              </div>
            )}
            {/* Rede de segurança (2026-09-15): a regra de palavra-chave achou sinal de
                reclamação na fala do cliente mas não teve confiança pra decidir sozinha
                (2+ sub-categorias bateram), então quem classificou foi a IA. Só mostra
                quando a IA NÃO concluiu RECLAMACAO — que é justamente o caso em que o
                aviso muda algo pro atendente; se já veio como reclamação, é redundante. */}
            {triagem?.sinal_subcategoria && triagem?.categoria !== 'RECLAMACAO' && (
              <div className={styles.alertaModerado} title="Palavras de reclamação encontradas na fala do cliente">
                ⚠️ Possível reclamação:{' '}
                {String(triagem.sinal_subcategoria).split(',').map(s => s.replace(/_/g, ' ')).join(' · ')}
              </div>
            )}
            {/* Entidades extraídas: nome, cidade, produtos */}
            {(triagem?.nome_cliente_detectado || triagem?.cidade_detectada ||
              (Array.isArray(triagem?.produtos_mencionados) && triagem.produtos_mencionados.length > 0)) && (
              <div className={styles.entidades}>
                {triagem.nome_cliente_detectado && (
                  <span className={styles.entidadeTag}>👤 {triagem.nome_cliente_detectado}</span>
                )}
                {triagem.cidade_detectada && (
                  <span className={styles.entidadeTag}>📍 {triagem.cidade_detectada}</span>
                )}
                {Array.isArray(triagem.produtos_mencionados) && triagem.produtos_mencionados.map((p, i) => (
                  <span key={i} className={styles.entidadeTag}>🛍 {p}</span>
                ))}
              </div>
            )}
          </div>
          <button id="octadesk-painel-fechar" className={styles.btnFechar} onClick={onFechar}><IconClose /></button>
        </div>

        {/* Bloco "Resposta sugerida pela IA" REMOVIDO em 2026-08-24 (pedido do dono:
            "não estamos usando mais"). Tinha a sugestão editável + Copiar + Aprovar e
            enviar. Como o botão de envio vivia DENTRO dele, o painel deixa de responder
            conversas — era o único caminho de envio pela tela. O endpoint
            POST /triagem/:sala/responder continua no backend, sem caller, caso volte. */}

        <div className={styles.painelMsgs}>
          {carregando && (
            <div className={styles.painelLoading}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`${styles.msgSkeleton} ${i % 2 === 0 ? styles.msgSkeletonLeft : styles.msgSkeletonRight}`} />
              ))}
            </div>
          )}
          {/* Carregar mais antigas — só aparece quando o limite atual foi atingido */}
          {!carregando && mensagens.length >= limiteMsgs && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <button
                onClick={carregarMaisAntigas}
                disabled={carregandoMais}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'var(--text-dim, #94a3b8)',
                  padding: '6px 14px',
                  borderRadius: 999,
                  fontSize: '0.78rem',
                  cursor: carregandoMais ? 'wait' : 'pointer'
                }}
              >
                {carregandoMais ? '⏳ Carregando...' : '↑ Carregar mais antigas'}
              </button>
            </div>
          )}
          {!carregando && mensagens.length === 0 && <p className={styles.semMsgs}>Nenhuma mensagem encontrada</p>}
          {!carregando && (() => {
            let ultimoDia = null;
            return mensagens.map((msg, i) => {
              // Cliente = esquerda | Empresa (bot + atendente) = direita
              // Detecta atendente humano por nome (sync não diferencia origem AGENT)
              const isEmpresa =
                msg.origem === 'BOT' ||
                msg.origem === 'AGENT' ||
                msg.fromMe ||
                isAtendente(msg.remetente);
              const isLeft    = !isEmpresa;
              const texto     = (msg.texto || '').replace(/<[^>]*>/g, '').trim();
              if (!texto) return null;

              // Separador de dia (estilo WhatsApp)
              const diaAtual = diaDaMsg(msg.hora);
              const mostrarSep = diaAtual && diaAtual !== ultimoDia;
              if (diaAtual) ultimoDia = diaAtual;

              // Verificar se esta mensagem menciona o produto filtrado
              const temProduto = filtroProduto &&
                texto.toUpperCase().includes(filtroProduto.toUpperCase());

              // Destacar o código do produto no texto
              const renderTexto = () => {
                if (!temProduto) return texto;
                const regex = new RegExp(`(${filtroProduto})`, 'gi');
                const partes = texto.split(regex);
                return partes.map((parte, pi) =>
                  parte.toUpperCase() === filtroProduto.toUpperCase()
                    ? <mark key={pi} style={{ background: '#fef08a', color: '#1e293b', borderRadius: 3, padding: '0 2px', fontWeight: 700 }}>{parte}</mark>
                    : parte
                );
              };

              // Label da empresa (bot ou atendente) — visual unificado
              const labelEmpresa = msg.origem === 'BOT'
                ? '🤖 Bot'
                : `💬 ${msg.remetente || 'Atendente'}`;

              return (
                <div key={msg.id || i} style={{ display: 'contents' }}>
                  {mostrarSep && (
                    <div className={styles.msgDiaSep}>
                      <span>{formatarDataSeparador(msg.hora)}</span>
                    </div>
                  )}
                  <div
                    ref={temProduto && !msgRefProduto.current ? msgRefProduto : null}
                    className={`${styles.msgBubble} ${isLeft ? styles.msgLeft : styles.msgRight} ${temProduto ? styles.msgDestaque : ''}`}
                  >
                    {isLeft  && <div className={styles.msgAutorTag}>{msg.remetente || nome}</div>}
                    {!isLeft && <div className={styles.msgAutorTag}>{labelEmpresa}</div>}
                    <div className={styles.msgTextoBalao}>{renderTexto()}</div>
                    <div className={styles.msgHora}>{formatarHora(msg.hora)}</div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </aside>
    </div>,
    document.body
  );
}

// ---- Componente Principal ----
export default function Octadesk() {
  const [metricas, setMetricas]             = useState(null);
  const [salas, setSalas]                   = useState([]);
  const [grupos, setGrupos]                 = useState([]);
  const [triagens, setTriagens]             = useState({}); // mapa: sala_key → triagem
  const [statusConexao, setStatus]          = useState('verificando');
  const [carregando, setCarregando]         = useState(true);
  const [carregandoSalas, setCarregandoSalas] = useState(true);
  const [filtroStatus, setFiltroStatus]     = useState('abertas');
  const [busca, setBusca]                   = useState('');
  const [page, setPage]                     = useState(1);
  const [totalPaginas, setTotalPaginas]     = useState(1);
  const [totalItems, setTotalItems]         = useState(0);
  const [ultimaAtt, setUltimaAtt]           = useState(null);
  const [erro, setErro]                     = useState(null);
  const [salaSelecionada, setSalaSelecionada] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState(''); // '' = todas
  const [filtroUrgencia,  setFiltroUrgencia]  = useState(''); // '' = todas
  const [filtroAgente,    setFiltroAgente]    = useState(''); // '' = todos
  const [filtroDia, setFiltroDia]           = useState(null); // YYYY-MM-DD ou null (via gráfico)
  const [modalExport, setModalExport]       = useState(false); // modal "Baixar conversas em .txt"
  const [dataInicio, setDataInicio]         = useState('');   // YYYY-MM-DD ou '' (filtro manual — aba Conversas)
  const [dataFim, setDataFim]               = useState('');   // YYYY-MM-DD ou '' (aba Conversas)
  // Período da aba Indicadores (2026-08-26, pedido do dono) — estado PRÓPRIO,
  // separado do período da aba Conversas: são filtros de telas diferentes (KPIs
  // agregados vs lista de conversas) e compartilhar o mesmo estado faria o
  // usuário mexer no período numa aba e ver a outra mudar sozinha sem ter tocado
  // nela. O componente/estilo do seletor é o mesmo (reaproveitado), o estado não.
  const [dataInicioPainel, setDataInicioPainel] = useState('');
  const [dataFimPainel, setDataFimPainel]       = useState('');
  const [filtroProduto, setFiltroProduto]   = useState(null); // código do produto ou null
  const [sortCol, setSortCol]               = useState(null);  // null | 'cliente' | 'msgs' | 'atualiza' | 'status'
  const [sortDir, setSortDir]               = useState('desc'); // 'asc' | 'desc'
  // Novos agentes
  const [insights, setInsights]             = useState({ resumo: null, sentimento: null, produtos: null });
  // Sprint 1 — Métricas de tempo (só reclamações abertas ainda usa) + reclamações detalhe
  const [metricasTempo, setMetricasTempo]   = useState(null);
  // Abas (2026-08-24, pedido do dono): 'painel' = cards + resultado do clique;
  // 'conversas' = a lista completa, sem os indicadores no caminho.
  const [aba, setAba] = useState('painel');
  // Resultado dos cards que antes FILTRAVAM a tabela (abertas, não respondidas,
  // reclamações abertas). Com a tabela em outra aba, eles passam a render aqui.
  // Um estado só para o modal de qualquer card: { tipo, titulo, emoji, cor, itens, carregando }
  const [modalCard, setModalCard] = useState(null);
  // Paginação dos painéis de drill-down: 50 por página (a lista pode passar de 500).
  const [paginaPainel, setPaginaPainel] = useState(1);
  const POR_PAGINA_PAINEL = 50;
  // Drill-down dos cards de tempo (2026-08-24): { tipo, titulo, dados, carregando }
  // Reclamações em 4 grupos de negócio (2026-08-24) + painel do grupo clicado
  const [reclamacoesGrupos, setReclamacoesGrupos] = useState([]);
  // Sprint 2
  const [leadsQuentes, setLeadsQuentes] = useState([]);
  const [produtosReclamacao, setProdutosReclamacao] = useState([]);
  const [gerandoResumo, setGerandoResumo]   = useState(false);
  const [dataResumo, setDataResumo]         = useState(new Date().toISOString().split('T')[0]); // data do resumo
  const LIMIT = 30;

  // Cursor de triagem para polling incremental — guarda o serverNow da última carga
  const triagemCursorRef = useRef(null);

  // Carrega triagens da IA — primeira chamada traz tudo; chamadas seguintes só os deltas
  const carregarTriagens = useCallback(async () => {
    try {
      const { mapa, serverNow } = await fetchOctadeskTriagem(triagemCursorRef.current);
      // Se for primeira carga (sem cursor), substitui o estado.
      // Se for delta, faz merge para preservar as triagens já em memória.
      if (triagemCursorRef.current) {
        if (Object.keys(mapa).length > 0) {
          setTriagens(prev => ({ ...prev, ...mapa }));
        }
      } else {
        setTriagens(mapa);
      }
      if (serverNow) triagemCursorRef.current = serverNow;
    } catch { /* silencioso */ }
  }, []);


  // Refs estáveis para callbacks dentro dos handlers do socket — evita o socket
  // ficar lendo a versão antiga das funções a cada render.
  // Inicializadas com null porque carregarSalas/carregarMetricas são declaradas
  // mais abaixo (useCallback) — atualizamos o .current num useEffect dedicado.
  const carregarSalasRef    = useRef(null);
  const carregarMetricasRef = useRef(null);

  useEffect(() => {
    fetchOctadeskStatus().then(d => setStatus(d.conectado ? 'conectado' : 'erro')).catch(() => setStatus('erro'));
    fetchOctadeskGrupos().then(r => { if (r.success) setGrupos(r.data || []); }).catch(() => {});
    carregarTriagens();
    // Fallback de segurança — refaz a triagem completa a cada 10min mesmo com socket
    // (caso o cliente perca eventos por desconexão). O fluxo principal é incremental.
    const t = setInterval(carregarTriagens, 10 * 60 * 1000);

    // ─── Socket.IO: push em tempo real ───────────────────────────────────────
    const socket = io(`${window.location.origin}/octadesk`, {
      transports: ['websocket', 'polling']
    });
    socket.on('connect', () => console.log('📞 [Octadesk] socket conectado'));
    socket.on('salas-atualizadas',     () => carregarSalasRef.current?.());
    socket.on('mensagens-atualizadas', () => carregarSalasRef.current?.());
    socket.on('triagem-nova',          () => carregarTriagens());

    // Só o que NÃO responde ao filtro de período: o Resumo do dia tem date picker
    // próprio, e reclamações-detalhe/produtos-com-reclamação não têm card visível.
    // Todo o resto (sentimento, produtos, leads, reclamações por motivo e métricas
    // de tempo) carrega no efeito dedicado ao período logo abaixo, que já cobre o
    // mount (dataInicioPainel/Fim vazios = sem filtro).
    Promise.all([
      fetchOctadeskResumoDiario(),
      fetchOctadeskReclamacoesDetalhe(),
      fetchOctadeskProdutosComReclamacao(),
    ]).then(([resumoData, reclData, prodReclData]) => {
      setInsights(prev => ({ ...prev, resumo: resumoData?.resumo || null }));
      setProdutosReclamacao(prodReclData?.data || []);
    }).catch(() => {});

    return () => {
      clearInterval(t);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Indicadores sensíveis a PERÍODO (2026-08-26, pedido do dono): sentimento,
  // produtos mencionados, leads quentes e reclamações por motivo. Roda no mount
  // (dataInicioPainel/Fim vazios = sem filtro = comportamento idêntico ao de
  // antes), de novo sempre que o período da aba Indicadores mudar, e no botão
  // "Atualizar" (via ref, mesmo padrão de carregarMetricasRef/carregarSalasRef).
  // "Reclamações Abertas" TAMBÉM entrou no filtro (2026-08-26, 2ª rodada — o
  // dono pediu explicitamente depois de ver o card fora do recorte). Semântica
  // com período: total = reclamações do período; abertas = quantas dessas ainda
  // estão abertas. Os dois números do mesmo recorte, então a proporção segue
  // coerente — era essa a ressalva original, e ela fica resolvida assim.
  const carregarIndicadoresPainelRef = useRef(null);
  const carregarIndicadoresPainel = useCallback(() => {
    const periodo = { dataInicio: dataInicioPainel || null, dataFim: dataFimPainel || null };
    return Promise.all([
      fetchOctadeskSentimento(null, periodo),
      fetchOctadeskMencoesProduto(10, null, periodo),
      fetchOctadeskLeadsQuentes(periodo),
      fetchOctadeskReclamacoesGrupos(periodo),
      // 2026-08-26 (2ª rodada): o dono pediu que "Reclamações Abertas" também
      // siga o período — antes era o único card fora do filtro (snapshot).
      fetchOctadeskMetricasTempo(periodo),
    ]).then(([sentData, prodData, leadsData, gruposData, tempoData]) => {
      setInsights(prev => ({
        ...prev,
        sentimento: sentData?.totais      || null,
        produtos:   prodData?.topProdutos || []
      }));
      setLeadsQuentes(leadsData?.data || []);
      setReclamacoesGrupos(gruposData?.data || []);
      setMetricasTempo(tempoData?.data || null);
    }).catch(() => {});
  }, [dataInicioPainel, dataFimPainel]);
  carregarIndicadoresPainelRef.current = carregarIndicadoresPainel;

  useEffect(() => { carregarIndicadoresPainel(); }, [carregarIndicadoresPainel]);

  const handleGerarResumo = async () => {
    // Se já há resumo para a data selecionada, pedir confirmação antes de sobrescrever
    if (insights.resumo && insights.resumo.data === dataResumo) {
      const ok = window.confirm(
        `Já existe um resumo para ${new Date(dataResumo + 'T12:00:00').toLocaleDateString('pt-BR')}.\n\nGerar novamente vai substituí-lo. Continuar?`
      );
      if (!ok) return;
    }
    setGerandoResumo(true);
    try {
      await fetchOctadeskGerarResumo(dataResumo);
      // Aguarda 3s e recarrega o resumo da data selecionada
      setTimeout(async () => {
        const r = await fetchOctadeskResumoDiario(dataResumo);
        if (r.success) setInsights(prev => ({ ...prev, resumo: r.resumo }));
        setGerandoResumo(false);
      }, 3000);
    } catch { setGerandoResumo(false); }
  };

  // Mudar a data do resumo — recarrega resumo já existente daquele dia
  const handleDataResumoChange = async (novaData) => {
    setDataResumo(novaData);
    setInsights(prev => ({ ...prev, resumo: null })); // limpa enquanto carrega
    try {
      const r = await fetchOctadeskResumoDiario(novaData);
      if (r.success) setInsights(prev => ({ ...prev, resumo: r.resumo }));
    } catch { /* silencioso */ }
  };

  const carregarMetricas = useCallback(async () => {
    setCarregando(true);
    try { const r = await fetchOctadeskMetricas(); if (r.success) setMetricas(r.data); }
    catch (e) { console.error(e); }
    finally { setCarregando(false); }
  }, []);

  // Ref para cancelar requisições anteriores de /salas quando uma nova chega
  const salasAbortRef = useRef(null);

  const carregarSalas = useCallback(async (
    pg = 1,
    filtro = filtroStatus,
    texto = busca,
    dia = filtroDia,
    produto = filtroProduto,
    cat = filtroCategoria,
    urg = filtroUrgencia,
    ag = filtroAgente,
    dInicio = dataInicio,
    dFim = dataFim,
    sCol = sortCol,
    sDir = sortDir
  ) => {
    // Cancela request anterior se ainda estiver em voo
    if (salasAbortRef.current) salasAbortRef.current.abort();
    const ac = new AbortController();
    salasAbortRef.current = ac;

    setCarregandoSalas(true); setErro(null);
    try {
      const statusParam = filtro === 'abertas' ? 0 : filtro === 'fechadas' ? 1 : null;
      const r = await fetchOctadeskSalas({
        page: pg,
        limit: produto ? 100 : LIMIT,
        status: produto ? null : statusParam,
        busca: texto,
        // filtroDia (gráfico, dia único) tem prioridade sobre o período manual
        data: dia || null,
        // período manual — só enviado quando não há filtroDia ativo
        dataInicio: !dia && dInicio ? dInicio : undefined,
        dataFim:    !dia && dFim    ? dFim    : undefined,
        produto,
        categoria: cat || null,
        urgencia:  urg || null,
        agente:    ag  || null,
        sort:      sCol || undefined,
        dir:       sCol ? sDir : undefined,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if (r.success) {
        setSalas(r.data || []);
        setTotalPaginas(r.paginacao?.totalPaginas || 1);
        setTotalItems(r.paginacao?.total || 0);
        setUltimaAtt(new Date());
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setErro('Não foi possível carregar as conversas.');
    } finally {
      if (!ac.signal.aborted) setCarregandoSalas(false);
    }
  }, [filtroStatus, busca, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir]);

  useEffect(() => { carregarMetricas(); carregarSalas(1, filtroStatus, busca, null); }, []);

  useEffect(() => {
    // Não atualiza automaticamente quando há filtro de dia ativo (evita sobrescrever).
    // O canal principal de atualização agora é o socket /octadesk; mantemos um
    // refresh a cada 5min como fallback se o cliente perder eventos.
    if (filtroDia) return;
    const t = setInterval(() => carregarSalas(page, filtroStatus, busca, null), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [page, filtroStatus, busca, filtroDia, carregarSalas]);

  // ── Recarrega a lista quando um filtro de IA muda (fix 2026-08-25) ──────────
  // Os chips de categoria/urgência e o select de agente só fazem setState — ao
  // contrário de status/busca/período, nenhum handler deles chama carregarSalas.
  // Sem este efeito, clicar em "Dúvida" só acendia o chip: a lista ficava igual até
  // o refresh de 5 minutos. Era o "nenhum filtro está funcionando" do dono.
  // O guard pula a primeira renderização (o mount já tem a carga inicial própria).
  const filtrosIAJaMontou = useRef(false);
  useEffect(() => {
    if (!filtrosIAJaMontou.current) { filtrosIAJaMontou.current = true; return; }
    setPage(1);
    carregarSalas(1, filtroStatus, busca, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroCategoria, filtroUrgencia, filtroAgente]);

  // Mantém os refs apontando para a versão mais recente das callbacks.
  // Declarado AQUI (depois de carregarSalas/carregarMetricas existirem) para
  // evitar TDZ — antes era no topo e estourava ReferenceError.
  carregarSalasRef.current    = carregarSalas;
  carregarMetricasRef.current = carregarMetricas;

  // Debounce do campo de busca via ref (em vez de window global)
  const buscaTimerRef = useRef(null);

  // ── handlePeriodo (RESTAURADO 2026-08-25) ───────────────────────────────────
  // Foi removido por engano junto com a limpeza do gráfico de atividade (o bloco
  // ficava logo acima dele) — os inputs de data continuaram chamando a função e cada
  // toque estourava ReferenceError: era o "filtro do período não funciona" do dono.
  const handlePeriodo = (campo, valor) => {
    const novoInicio = campo === 'inicio' ? valor : dataInicio;
    const novoFim    = campo === 'fim'    ? valor : dataFim;
    setDataInicio(novoInicio);
    setDataFim(novoFim);
    setFiltroDia(null); // período manual substitui o filtro de dia (legado do gráfico)
    setPage(1);
    carregarSalas(1, filtroStatus, busca, null, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, novoInicio, novoFim, sortCol, sortDir);
  };

  // ── Exportar conversas em .txt ────────────────────────────────────────────
  // O botão abre o modal (ModalExportarConversas), que é quem monta a query e
  // baixa. Aqui só se prepara o ESTADO INICIAL: o modal abre com os mesmos
  // filtros da lista — é o recorte que o usuário acabou de olhar — e de lá ele
  // muda o que quiser sem mexer na tela.
  const padraoExport = useMemo(() => ({
    status: filtroStatus === 'abertas' ? 'abertas' : filtroStatus === 'fechadas' ? 'fechadas' : 'todas',
    busca,
    categorias: filtroCategoria ? [filtroCategoria] : [],
    urgencias:  filtroUrgencia  ? [filtroUrgencia]  : [],
    // A lista da tela é de escolha única; no modal o filtro é múltiplo — entra
    // como seleção de um item só, e lá dentro dá para acrescentar outros.
    agentes: filtroAgente ? [valAgente(filtroAgente)] : [],
    // filtroDia (clique no gráfico) tem prioridade, igual ao carregarSalas
    dataInicio: filtroDia || dataInicio || '',
    dataFim:    filtroDia || dataFim    || '',
    falante: 'todos',
    disparos: true,
  }), [filtroStatus, busca, filtroCategoria, filtroUrgencia, filtroAgente, filtroDia, dataInicio, dataFim]);

  // Período da aba Indicadores — só seta o estado; o useEffect [dataInicioPainel,
  // dataFimPainel] dispara os 4 fetches sensíveis a período (não precisa chamar
  // nada aqui, ao contrário do handlePeriodo da aba Conversas).
  const handlePeriodoPainel = (campo, valor) => {
    if (campo === 'inicio') setDataInicioPainel(valor);
    else setDataFimPainel(valor);
  };
  const limparPeriodoPainel = () => { setDataInicioPainel(''); setDataFimPainel(''); };

  // Atalhos de período (2026-08-26, pedido do dono) — mesma ideia dos atalhos do
  // FaturamentoHeader (Mês atual / Mês passado / Últimos 30 dias), adaptados aqui.
  // Datas em horário LOCAL: toISOString() devolve UTC e, em UTC-3, empurra o dia
  // pra frente depois das 21h (armadilha já documentada no FaturamentoHeader).
  // "Semana passada" = segunda a domingo da semana anterior (semana CHEIA, não
  // "7 dias atrás") — é o recorte que faz sentido pra comparar semana com semana.
  const atalhosPeriodo = useMemo(() => {
    const isoLocal = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const hoje = new Date();
    const y = hoje.getFullYear();
    const m = hoje.getMonth();
    const d = hoje.getDate();
    // getDay(): 0=domingo. Converte pra 0=segunda pra montar a semana cheia.
    const offsetSegunda = (hoje.getDay() + 6) % 7;
    const segundaAtual = new Date(y, m, d - offsetSegunda);
    // Deriva do próprio objeto (não de y/m originais): a segunda pode ter caído
    // no mês anterior, e aí reusar y/m daria data errada.
    const sy = segundaAtual.getFullYear(), sm = segundaAtual.getMonth(), sd = segundaAtual.getDate();
    return [
      { rot: 'Hoje',            de: isoLocal(hoje),                    ate: isoLocal(hoje) },
      { rot: 'Últimos 7 dias',  de: isoLocal(new Date(y, m, d - 6)),   ate: isoLocal(hoje) },
      { rot: 'Semana passada',  de: isoLocal(new Date(sy, sm, sd - 7)), ate: isoLocal(new Date(sy, sm, sd - 1)) },
      { rot: 'Mês atual',       de: isoLocal(new Date(y, m, 1)),       ate: isoLocal(hoje) },
      { rot: 'Mês passado',     de: isoLocal(new Date(y, m - 1, 1)),   ate: isoLocal(new Date(y, m, 0)) },
      { rot: 'Últimos 30 dias', de: isoLocal(new Date(y, m, d - 29)),  ate: isoLocal(hoje) },
    ];
  }, []);

  // Seta os dois de uma vez: o React agrupa num único re-render, então o
  // useEffect [dataInicioPainel, dataFimPainel] dispara UMA busca só.
  const aplicarAtalhoPeriodo = (de, ate) => { setDataInicioPainel(de); setDataFimPainel(ate); };

  const handleFiltro   = (f) => { setFiltroStatus(f); setPage(1); carregarSalas(1, f, busca, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir); };

  const handleSort = (col) => {
    // cliente começa asc (alfabético faz mais sentido); demais começam desc
    const defaultDir = col === 'cliente' ? 'asc' : 'desc';
    const novoDir = sortCol === col
      ? (sortDir === 'asc' ? 'desc' : 'asc')
      : defaultDir;
    setSortCol(col);
    setSortDir(novoDir);
    setPage(1);
    carregarSalas(1, filtroStatus, busca, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, col, novoDir);
  };
  const handleBusca    = (e) => {
    const t = e.target.value; setBusca(t); setPage(1);
    // Procurar um cliente varre TODO o acervo, aberto ou fechado (99% das
    // conversas estão fechadas — buscar dentro de "Abertas" acharia quase nada).
    // O backend já ignora o status quando há termo; aqui o chip acompanha, para a
    // tela não dizer "Abertas" enquanto mostra conversa fechada.
    const statusDaBusca = t.trim() ? 'todas' : filtroStatus;
    if (t.trim() && filtroStatus !== 'todas') setFiltroStatus('todas');
    if (buscaTimerRef.current) clearTimeout(buscaTimerRef.current);
    buscaTimerRef.current = setTimeout(
      () => carregarSalas(1, statusDaBusca, t, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir),
      500
    );
  };
  const handlePagina   = (p) => { setPage(p); carregarSalas(p, filtroStatus, busca, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleRefresh  = () => { carregarMetricas(); carregarSalas(page, filtroStatus, busca, filtroDia, filtroProduto, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir); carregarTriagens(); carregarIndicadoresPainelRef.current?.(); };

  // ── Drill-down dos cards (2026-08-24, pedido do dono: todo card leva aos dados) ──
  // Cards que viram FILTRO da tabela abaixo rolam até ela — sem isso o clique parece
  // não ter feito nada, porque a tabela fica fora da área visível.

  // Cards que representam um RECORTE de conversas (abertas, não respondidas,
  // reclamações abertas). Antes filtravam a tabela e rolavam até ela; com a tabela
  // movida para a aba "Conversas", passam a carregar o resultado num painel aqui —
  // assim o clique não tira o usuário da visão de indicadores.
  // ── Modal dos cards (2026-08-24) ──────────────────────────────────────────
  // Um estado só para todos os cards: cada um carrega seu recorte e abre o mesmo
  // modal. Antes havia 5 estados de painel independentes, e como cada handler
  // fechava só alguns dos outros, dava pra ter dois abertos ao mesmo tempo.
  const fecharModal = () => { setModalCard(null); setPaginaPainel(1); };

  const abrirModal = async (cfg, carregar) => {
    setPaginaPainel(1);
    setModalCard({ ...cfg, itens: [], carregando: true });
    const itens = await carregar();
    setModalCard({ ...cfg, itens: itens || [], carregando: false });
  };

  // Recorte de conversas (abertas, não respondidas, reclamações em aberto)
  const abrirModalSalas = ({ titulo, emoji, cor, status = 0, categoria = '', ordenarPor = null, subtitulo = null }) =>
    abrirModal({ tipo: 'salas', titulo, emoji, cor, subtitulo }, async () => {
      const r = await fetchOctadeskSalas({
        page: 1, limit: 500, status,
        categoria: categoria || null,
        sort: ordenarPor || null, dir: ordenarPor ? 'desc' : null
      });
      return r?.data || [];
    });

  const abrirModalGrupo = (g) => {
    // Resumo clicável no topo (mesmo padrão dos leads, pedido do dono 2026-08-25):
    // Atendimento filtra por sentimento (Ruins/Elogios); os demais, por sub-categoria.
    const resumo = g.chave === 'atendimento'
      ? [
          { campo: 'sentimento', chave: 'INSATISFEITO', rotulo: 'Ruins', valor: g.negativos || 0 },
          { campo: 'sentimento', chave: 'SATISFEITO', rotulo: 'Elogios', valor: g.positivos || 0 },
        ].filter(x => x.valor > 0)
      : (g.detalhe || []).map(d => ({
          campo: 'sub_categoria', chave: d.sub, rotulo: getSubcatCfg(d.sub).label, valor: d.total
        }));
    return abrirModal(
      { tipo: 'grupo', titulo: g.rotulo, emoji: g.emoji, cor: g.cor, resumo,
        subtitulo: g.chave === 'atendimento'
          ? 'Inclui elogios: são opiniões sobre atendimento, não só reclamações.'
          : null },
      // Mesmo período do card (2026-08-26) — senão o drill-down mostraria uma
      // lista maior/menor que o total que o usuário acabou de ver no card.
      async () => (await fetchOctadeskReclamacoesGrupoSalas(g.chave, 500, {
        dataInicio: dataInicioPainel || null, dataFim: dataFimPainel || null
      }))?.data || []
    );
  };

  const abrirModalLeads = () => {
    // A quebra por sinal aparece SÓ aqui no modal (pedido do dono 2026-08-25):
    // no card ficava permanente e ocupava metade do bloco de destaque.
    const conta = (c) => leadsQuentes.filter(l => l[`sinal_${c}`]).length;
    const resumo = [
      { campo: 'sinal', chave: 'revenda', rotulo: 'Revenda', valor: conta('revenda') },
      { campo: 'sinal', chave: 'atacado', rotulo: 'Atacado', valor: conta('atacado') },
      { campo: 'sinal', chave: 'catalogo', rotulo: 'Catálogo', valor: conta('catalogo') },
      { campo: 'sinal', chave: 'anuncio', rotulo: 'Anúncio (7 dias)', valor: conta('anuncio') },
    ].filter(x => x.valor > 0);
    return abrirModal(
      { tipo: 'leads', titulo: 'Leads quentes', emoji: '🔥', cor: '#22c55e', resumo,
        subtitulo: 'Detectados pelo que o cliente escreveu: revenda, atacado, catálogo, anúncio.' },
      async () => leadsQuentes
    );
  };

  // Clique em produto mencionado
  const handleProdutoClick = (codigo) => {
    if (filtroProduto === codigo) {
      // Toggle: limpar filtro
      setFiltroProduto(null);
      setFiltroDia(null);
      setPage(1);
      carregarSalas(1, filtroStatus, busca, null, null, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir);
    } else {
      setFiltroProduto(codigo);
      setFiltroDia(null);
      setPage(1);
      carregarSalas(1, 'todas', busca, null, codigo, filtroCategoria, filtroUrgencia, filtroAgente, dataInicio, dataFim, sortCol, sortDir);
    }
  };

  // A filtragem por categoria/urgência agora acontece no servidor (rota /salas com JOIN
  // em octadesk_triagem). Mantemos a referência abaixo para minimizar mudanças na renderização.
  const salasFiltradas = salas;

  return (
    <div className={styles.page}>
      {/* Cabeçalho */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><IconWhatsApp /></div>
          <div>
            <h1 className={styles.titulo}>Octadesk</h1>
            <p className={styles.subtitulo}>Central de Atendimento WhatsApp</p>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={`${styles.statusConexao} ${styles[`status_${statusConexao}`]}`}>
            <span className={styles.statusDot} />
            {statusConexao === 'conectado' ? 'Conectado' : statusConexao === 'erro' ? 'Erro' : 'Verificando...'}
          </div>
          {ultimaAtt && <span className={styles.ultimaAtt}>Atualizado às {ultimaAtt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>}
          <a
            href="/octadesk/regras"
            className={styles.btnRegras}
            title="Frases que marcam a conversa como reclamação sem gastar IA"
          >
            🔴 Palavras de Reclamação
          </a>
          <button id="octadesk-refresh-btn" className={styles.btnRefresh} onClick={handleRefresh}><IconRefresh /> Atualizar</button>
        </div>
      </header>

      {/* Abas (2026-08-24, pedido do dono): separa INDICADORES de LISTA.
          Antes tudo vivia na mesma pagina rolante: os cards no topo e a tabela de
          conversas la embaixo, com os paineis de drill-down espremidos no meio. */}
      <div className={styles.abas}>
        <button
          className={`${styles.aba} ${aba === 'painel' ? styles.abaAtiva : ''}`}
          onClick={() => setAba('painel')}
        >
          📊 Indicadores
        </button>
        <button
          className={`${styles.aba} ${aba === 'conversas' ? styles.abaAtiva : ''}`}
          onClick={() => setAba('conversas')}
        >
          💬 Conversas
        </button>
      </div>


      {aba === 'painel' && (
        <>
        {/* Filtro de PERÍODO da aba Indicadores (2026-08-26, pedido do dono) —
            mesmo componente/estilo do filtro Período da aba Conversas, estado
            próprio (dataInicioPainel/dataFimPainel, ver comentário na declaração
            do estado). Afeta: Sentimento, Produtos mencionados, Leads Quentes e
            Reclamações por motivo. NÃO afeta "Reclamações Abertas" — é o estado
            da fila AGORA, não um recorte do passado (selo "agora" no card). */}
        <div className={styles.filtrosIA} style={{ marginBottom: 12 }}>
          <span className={styles.filtrosIALabel}>Período dos indicadores:</span>
          <input
            type="date"
            className={styles.resumoDatePicker}
            value={dataInicioPainel}
            max={dataFimPainel || new Date().toISOString().split('T')[0]}
            onChange={e => handlePeriodoPainel('inicio', e.target.value)}
            title="Data de início do período"
            aria-label="Período dos indicadores — de"
          />
          <span className={styles.filtrosIALabel} style={{ margin: '0 2px' }}>–</span>
          <input
            type="date"
            className={styles.resumoDatePicker}
            value={dataFimPainel}
            min={dataInicioPainel || undefined}
            max={new Date().toISOString().split('T')[0]}
            onChange={e => handlePeriodoPainel('fim', e.target.value)}
            title="Data de fim do período"
            aria-label="Período dos indicadores — até"
          />
          {/* Atalhos (2026-08-26) — o chip fica destacado quando o período atual
              bate exatamente com ele, pra dar feedback de qual recorte está no ar. */}
          <span className={styles.filtrosIASep}>|</span>
          {atalhosPeriodo.map(a => (
            <button
              key={a.rot}
              className={`${styles.chipIA} ${dataInicioPainel === a.de && dataFimPainel === a.ate ? styles.chipIAAtivo : ''}`}
              onClick={() => aplicarAtalhoPeriodo(a.de, a.ate)}
              title={`${a.de.split('-').reverse().join('/')} a ${a.ate.split('-').reverse().join('/')}`}
            >
              {a.rot}
            </button>
          ))}
          {(dataInicioPainel || dataFimPainel) && (
            <button className={styles.chipIALimpar} onClick={limparPeriodoPainel}>
              ✕ Limpar período
            </button>
          )}
        </div>

        {/* Indicadores (layout definido pelo dono em 2026-08-25) — dois cards de
            destaque em cima e a quebra por motivo logo abaixo. */}
        <section className={styles.destaqueGrid}>
          <CardDestaque
            titulo="Reclamações Abertas"
            icone={<IconAlert />}
            cor="#ef4444"
            valor={metricasTempo?.reclamacoes?.abertas || 0}
            /* Com período ativo os dois números são do MESMO recorte: "detectadas
               no período" e, dessas, quantas seguem abertas. O rótulo diz qual
               dos dois casos está no ar pra não confundir com o total histórico. */
            complemento={`de ${(metricasTempo?.reclamacoes?.total || 0).toLocaleString('pt-BR')} detectadas${dataInicioPainel && dataFimPainel ? ' no período' : ''}`}
            proporcao={metricasTempo?.reclamacoes?.total
              ? (metricasTempo.reclamacoes.abertas / metricasTempo.reclamacoes.total) * 100
              : 0}
            legenda={metricasTempo?.reclamacoes?.total
              ? `${((metricasTempo.reclamacoes.abertas / metricasTempo.reclamacoes.total) * 100).toFixed(1).replace('.', ',')}% do total ainda em aberto`
              : null}
            carregando={carregando}
            onClick={() => abrirModalSalas({ titulo: 'Reclamações em aberto', emoji: '⚠️', cor: '#ef4444', status: 0, categoria: 'RECLAMACAO' })}
          />
          <CardDestaque
            titulo="Leads Quentes"
            icone={<IconUser />}
            cor="#16a34a"
            valor={leadsQuentes.length}
            complemento={
              dataInicioPainel && dataFimPainel
                ? `${new Date(dataInicioPainel + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} – ${new Date(dataFimPainel + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`
                : 'últimos 7 dias'
            }
            legenda={(() => {
              const fechadas = leadsQuentes.filter(l => l.conversa_fechada).length;
              return fechadas ? `${fechadas} já encerradas — clique para ver por sinal` : 'clique para ver por sinal';
            })()}
            carregando={carregando}
            onClick={abrirModalLeads}
          />
        </section>

        {reclamacoesGrupos.length > 0 && (
          <section className={styles.blocoReclamacoes}>
            <h3 className={styles.blocoReclamacoesTitulo}>
              Reclamações por motivo
              <span className={styles.blocoReclamacoesDica}>clique em um card para ver as conversas</span>
            </h3>
            <div className={styles.metricsGridReclamacoes}>
              {reclamacoesGrupos.map(g => (
                <CardMotivo
                  key={g.chave}
                  titulo={g.rotulo}
                  icone={<span style={{ fontSize: '0.95rem' }}>{g.emoji}</span>}
                  cor={g.cor}
                  valor={g.total}
                  /* A quebra por sub-categoria vira LISTA (rótulo à esquerda, número à
                     direita) em vez da frase corrida de antes — dá pra bater o olho e
                     comparar. Atendimento é o único que não sai de sub-categoria: mostra
                     a divisão entre críticas e elogios. */
                  itens={g.chave === 'atendimento'
                    ? [
                        { rotulo: 'Ruins', valor: g.negativos || 0 },
                        { rotulo: 'Elogios', valor: g.positivos || 0 },
                      ]
                    : (g.detalhe || []).slice(0, 3).map(d => ({
                        rotulo: getSubcatCfg(d.sub).label,
                        valor: d.total
                      }))}
                  carregando={carregando}
                  onClick={() => abrirModalGrupo(g)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Painel "Reclamações por Motivo" REMOVIDO em 2026-08-24 (pedido do dono).
            Mostrava as 13 sub-categorias técnicas da IA (site_erro, login_cadastro,
            produto_errado...) em cards separados — poluído e redundante desde que os
            4 cards de negócio (Erro no Site / Produtos / Atendimento / Outros) passaram
            a resumir a mesma informação lá em cima, já clicáveis.
            A quebra por sub-categoria continua visível: aparece no subtítulo de cada card
            e nos selos da lista que abre ao clicar. O endpoint /reclamacoes-detalhe segue
            no ar — nada foi removido do backend. */}


        {/* Gráfico "Atividade — Últimos 7 Dias" REMOVIDO em 2026-08-24 (pedido do dono).
            Era o unico ponto que setava `filtroDia` (clique numa barra filtrava a tabela
            por aquele dia), por isso sairam junto o componente AtividadeChart, o handler
            handleDiaClick e o banner de dia ativo. O filtro por PERIODO (dataInicio/dataFim)
            continua disponivel e cobre o mesmo caso de uso. */}

        {/* ── Painel de Insights dos Agentes IA ── */}
        <section className={styles.insightsRow}>

          {/* Card: Resumo Diário */}
          <div className={styles.insightCardResumo}>
            <div className={styles.insightCardHeader}>
              <h3 className={styles.insightCardTitulo}>Resumo do dia</h3>
              <div className={styles.resumoControles}>
                <input
                  type="date"
                  className={styles.resumoDatePicker}
                  value={dataResumo}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => handleDataResumoChange(e.target.value)}
                  title="Selecionar data do resumo"
                />
                <button
                  className={styles.insightBtnGerar}
                  onClick={handleGerarResumo}
                  disabled={gerandoResumo}
                  title={`Gerar resumo de ${new Date(dataResumo + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`}
                >
                  {gerandoResumo ? '⏳ Gerando...' : '✨ Gerar'}
                </button>
              </div>
            </div>
            {insights.resumo ? (
              <>
                <p className={styles.insightResumoTexto}>{insights.resumo.resumo_texto}</p>
                <div className={styles.insightResumometa}>
                  <span>💬 {insights.resumo.total_conversas} conversas</span>
                  <span>⚠️ {insights.resumo.reclamacoes} reclamações</span>
                  <span className={styles.insightResumoData}>
                    {new Date(insights.resumo.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </>
            ) : (
              <p className={styles.insightVazio}>
                {gerandoResumo
                  ? '⏳ Gerando resumo...'
                  : `Nenhum resumo para ${new Date(dataResumo + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}. Clique em "\u2728 Gerar".`
                }
              </p>
            )}
          </div>


          {/* Card: Sentimento Geral */}
          <div className={styles.insightCard}>
            <div className={styles.insightCardHeader}>
              <h3 className={styles.insightCardTitulo}>Sentimento dos clientes</h3>
            </div>
            {insights.sentimento && (insights.sentimento.SATISFEITO + insights.sentimento.INSATISFEITO + insights.sentimento.NEUTRO) > 0 ? (() => {
              const s = insights.sentimento;
              const tot = s.SATISFEITO + s.INSATISFEITO + s.NEUTRO || 1;
              const pSat  = Math.round((s.SATISFEITO  / tot) * 100);
              const pInsat = Math.round((s.INSATISFEITO / tot) * 100);
              const pNeut = Math.round((s.NEUTRO       / tot) * 100);
              return (
                <div className={styles.sentimentoWrap}>
                  {/* Total sob o titulo + legenda em linhas com % a direita (mockup 2026-08-25) */}
                  <p className={styles.sentimentoTotal}>{tot.toLocaleString('pt-BR')} conversas analisadas</p>
                  <div className={styles.sentimentoBarra}>
                    <div className={styles.sentimentoSegSat}   style={{ width: `${pSat}%`  }} title={`Satisfeito: ${pSat}%`}  />
                    <div className={styles.sentimentoSegNeut}  style={{ width: `${pNeut}%` }} title={`Neutro: ${pNeut}%`}     />
                    <div className={styles.sentimentoSegInsat} style={{ width: `${pInsat}%`}} title={`Insatisfeito: ${pInsat}%`} />
                  </div>
                  <div className={styles.sentimentoLegenda}>
                    <span className={styles.sentimentoLegItem}><span className={styles.sentimentoDot} style={{ background: '#22c55e' }} />Satisfeitos<b>{pSat}%</b></span>
                    <span className={styles.sentimentoLegItem}><span className={styles.sentimentoDot} style={{ background: '#94a3b8' }} />Neutros<b>{pNeut}%</b></span>
                    <span className={styles.sentimentoLegItem}><span className={styles.sentimentoDot} style={{ background: '#ef4444' }} />Insatisfeitos<b>{pInsat}%</b></span>
                  </div>
                </div>
              );
            })() : (
              <p className={styles.insightVazio}>Aguardando análise das conversas fechadas...</p>
            )}
          </div>

          {/* Card: Top Produtos Mencionados */}
          <div className={styles.insightCard}>
            <div className={styles.insightCardHeader}>
              <h3 className={styles.insightCardTitulo}>Produtos mencionados</h3>
              {filtroProduto && (
                <button className={styles.insightBtnGerar} onClick={() => handleProdutoClick(filtroProduto)}
                  style={{ borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
                >
                  ✕ {filtroProduto}
                </button>
              )}
            </div>
            {insights.produtos && insights.produtos.length > 0 ? (
              <ul className={styles.produtosList}>
                {insights.produtos.slice(0, 8).map((p, i) => (
                  <li
                    key={p.produto_codigo}
                    className={`${styles.produtosItem} ${filtroProduto === p.produto_codigo ? styles.produtosItemAtivo : ''}`}
                    onClick={() => handleProdutoClick(p.produto_codigo)}
                    role="button"
                    tabIndex={0}
                    title={`Ver conversas que mencionaram ${p.produto_codigo}`}
                    onKeyDown={e => e.key === 'Enter' && handleProdutoClick(p.produto_codigo)}
                  >
                    <span className={styles.produtosRank}>#{i + 1}</span>
                    <span className={styles.produtosCodigo}>{p.produto_codigo}</span>
                    <span className={styles.produtosNome}>{p.produto_nome || '—'}</span>
                    <span className={styles.produtosCount}>{p.total_mencoes}×</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.insightVazio}>Nenhum produto detectado nas conversas ainda.</p>
            )}
          </div>

        </section>
        </>
      )}

      {aba === 'conversas' && (
        <>
        {grupos.length > 0 && (
          <section className={styles.gruposRow}>
            {grupos.map(g => <span key={g.id || g._id} className={styles.grupoBadge}>{g.name || g.nome}</span>)}
          </section>
        )}


        {/* Tabela */}
        <section className={styles.tabelaSection}>
          {/* Banner de filtro de produto ativo */}
          {filtroProduto && (
            <div className={styles.filtroDiaBanner} style={{ background: 'rgba(37,211,102,0.08)', borderColor: 'rgba(37,211,102,0.25)' }}>
              <span>📦 Conversas que mencionaram <strong>{filtroProduto}</strong> — {totalItems} conversa{totalItems !== 1 ? 's' : ''}</span>
              <button className={styles.filtroDiaBannerLimpar} onClick={() => handleProdutoClick(filtroProduto)}>✕ Ver todas</button>
            </div>
          )}
          <div className={styles.tabelaToolbar}>
            <div className={styles.filtrosStatus}>
              {['abertas', 'fechadas', 'todas'].map(f => (
                <button key={f} id={`octadesk-filtro-${f}`}
                  className={`${styles.filtroBtnStatus} ${filtroStatus === f ? styles.filtroAtivo : ''}`}
                  onClick={() => handleFiltro(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className={styles.searchWrap}>
              <IconSearch />
              <input id="octadesk-busca" type="text" className={styles.searchInput}
                placeholder="Buscar cliente ou telefone..." value={busca} onChange={handleBusca} />
            </div>
            <span className={styles.totalLabel}>
              {totalItems.toLocaleString('pt-BR')} conversa{totalItems !== 1 ? 's' : ''}
              {busca.trim() && (
                <span style={{ marginLeft: 6, fontSize: '0.72rem', opacity: 0.75 }}>
                  · buscando em abertas e fechadas
                </span>
              )}
            </span>
          </div>

          {/* Filtros de Triagem IA + Período */}
          <div className={styles.filtrosIA}>
            <span className={styles.filtrosIALabel}>Filtrar por IA</span>
            {/* Período DE–ATÉ */}
            <span className={styles.filtrosIALabel} style={{ marginLeft: 4 }}>Período:</span>
            <input
              type="date"
              className={styles.resumoDatePicker}
              value={dataInicio}
              max={dataFim || new Date().toISOString().split('T')[0]}
              onChange={e => handlePeriodo('inicio', e.target.value)}
              title="Data de início do período"
              aria-label="Período de"
            />
            <span className={styles.filtrosIALabel} style={{ margin: '0 2px' }}>–</span>
            <input
              type="date"
              className={styles.resumoDatePicker}
              value={dataFim}
              min={dataInicio || undefined}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => handlePeriodo('fim', e.target.value)}
              title="Data de fim do período"
              aria-label="Período até"
            />
            {/* Abre o modal de exportação (período, status, triagem, urgência,
                agente, quem falou...). Antes baixava direto com os filtros da tela. */}
            <button
              className={styles.chipIA}
              onClick={() => setModalExport(true)}
              style={{ marginLeft: 6, cursor: 'pointer' }}
              title="Escolher os filtros e baixar as conversas em .txt"
            >
              ⬇️ Baixar .txt
            </button>
            <span className={styles.filtrosIASep}>|</span>
            {/* Categorias */}
            {[
              { v: '',               label: 'Todas' },
              { v: 'DUVIDA_PRODUTO', label: '🔵 Dúvida' },
              { v: 'PRECO',          label: '🟣 Preço' },
              { v: 'PEDIDO',         label: '🟢 Pedido' },
              { v: 'RECLAMACAO',     label: '🔴 Reclamação' },
              { v: 'SAUDACAO',       label: '⚪ Saudação' },
              { v: 'OUTRO',          label: '🟤 Outro' },
            ].map(({ v, label }) => (
              <button key={v || 'todas-cat'}
                className={`${styles.chipIA} ${filtroCategoria === v ? styles.chipIAAtivo : ''}`}
                onClick={() => setFiltroCategoria(filtroCategoria === v ? '' : v)}>
                {label}
              </button>
            ))}
            <span className={styles.filtrosIASep}>|</span>
            {/* Urgência */}
            {[
              { v: 'ALTA',  label: '🔴 Alta' },
              { v: 'MEDIA', label: '🟡 Média' },
              { v: 'BAIXA', label: '🟢 Baixa' },
            ].map(({ v, label }) => (
              <button key={v}
                className={`${styles.chipIA} ${filtroUrgencia === v ? styles.chipIAAtivo : ''}`}
                onClick={() => setFiltroUrgencia(filtroUrgencia === v ? '' : v)}>
                {label}
              </button>
            ))}
            {/* Filtro de agente — populado pelo ranking de agentes */}
            {metricas?.rankingAgentes?.length > 0 && (
              <>
                <span className={styles.filtrosIASep}>|</span>
                <select
                  className={styles.chipIA}
                  value={filtroAgente}
                  onChange={e => setFiltroAgente(e.target.value)}
                  style={{ cursor: 'pointer', padding: '4px 10px' }}
                  title="Filtrar por agente atribuído"
                >
                  <option value="">👤 Todos os agentes</option>
                  {metricas.rankingAgentes.map(a => (
                    <option key={a.nome} value={a.nome}>{a.nome} ({a.total})</option>
                  ))}
                </select>
              </>
            )}
            {(filtroCategoria || filtroUrgencia || filtroAgente || dataInicio || dataFim) && (
              <button className={styles.chipIALimpar}
                onClick={() => {
                  setFiltroCategoria(''); setFiltroUrgencia(''); setFiltroAgente('');
                  setDataInicio(''); setDataFim('');
                  setSortCol(null); setSortDir('desc');
                  setPage(1);
                  carregarSalas(1, filtroStatus, busca, filtroDia, filtroProduto, '', '', '', '', '', null, 'desc');
                }}>
                ✕ Limpar filtros
              </button>
            )}
          </div>

          {erro ? <div className={styles.erroMsg}>{erro}</div> : (
            <div className={styles.tabelaWrapper}>
              <table className={styles.tabela}>
                <thead>
                  <tr>
                    <th
                      style={{ width: '220px', cursor: 'pointer', userSelect: 'none' }}
                      className={styles.thOrdenavel}
                      onClick={() => handleSort('cliente')}
                      title="Ordenar por cliente"
                    >
                      Cliente / Telefone
                      <span className={styles.sortSeta} aria-hidden="true">
                        {sortCol === 'cliente' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </span>
                    </th>
                    <th>Resumo</th>
                    <th style={{ width: '130px' }}>Triagem IA</th>
                    <th
                      style={{ width: '90px', cursor: 'pointer', userSelect: 'none' }}
                      className={styles.thOrdenavel}
                      onClick={() => handleSort('status')}
                      title="Ordenar por status"
                    >
                      Status
                      <span className={styles.sortSeta} aria-hidden="true">
                        {sortCol === 'status' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </span>
                    </th>
                    <th
                      style={{ width: '70px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                      className={styles.thOrdenavel}
                      onClick={() => handleSort('msgs')}
                      title="Ordenar por mensagens não lidas"
                    >
                      Msgs
                      <span className={styles.sortSeta} aria-hidden="true">
                        {sortCol === 'msgs' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </span>
                    </th>
                    <th style={{ width: '96px' }} title="Data da primeira mensagem da conversa">Iniciada</th>
                    <th
                      style={{ width: '112px', cursor: 'pointer', userSelect: 'none', paddingRight: 20 }}
                      className={styles.thOrdenavel}
                      onClick={() => handleSort('atualiza')}
                      title="Ordenar por última atividade"
                    >
                      Atualizado
                      <span className={styles.sortSeta} aria-hidden="true">
                        {sortCol === 'atualiza' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {carregandoSalas
                    ? Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className={styles.skeletonRow}>
                        {Array.from({ length: 7 }).map((__, j) => <td key={j}><span className={styles.cellSkeleton} /></td>)}
                      </tr>
                    ))
                    : salasFiltradas.length === 0
                      ? <tr><td colSpan={7} className={styles.emptyCell}>Nenhuma conversa encontrada</td></tr>
                      : salasFiltradas.map(sala => {
                        const naoLidas = sala.unreadMessages || 0;
                        const tel      = extrairTelefone(sala);
                        const msg      = extrairTextoMensagem(sala);
                        const nome     = extrairNome(sala);
                        const salaKey  = sala.key || sala._id;
                        const triagem  = triagens[salaKey] || null;
                        return (
                          <tr key={salaKey}
                            className={`${styles.tabelaRow} ${naoLidas > 0 && sala.status === 0 ? styles.rowDestaque : ''}`}
                            onClick={() => setSalaSelecionada(sala)}
                            title="Clique para ver a conversa"
                          >
                            <td>
                              <div className={styles.clienteCell}>
                                <span className={styles.avatarCircle}>{nome.charAt(0).toUpperCase()}</span>
                                <div className={styles.clienteInfo}>
                                  <span className={styles.clienteNome}>{nome}</span>
                                  {tel
                                    ? <a href={`tel:${tel}`} className={styles.clienteTel} onClick={e => e.stopPropagation()}><IconPhone /> {tel}</a>
                                    : <span className={styles.clienteTelVazio}>Sem telefone</span>
                                  }
                                </div>
                              </div>
                            </td>
                            <td className={styles.msgCell}>
                              {/* Resumo da IA (mesmo campo já gerado pela triagem, sem custo
                                  incremental) em vez do texto cru da última mensagem — passa
                                  a mostrar do que a conversa trata, não só a última fala.
                                  title = texto cru da última mensagem, pra quem quiser ver. */}
                              <span className={styles.msgTexto} title={msg}>{triagem?.resumo || msg}</span>
                            </td>
                            <td>
                              {triagem
                                ? <TriagemBadge triagem={triagem} />
                                : (() => {
                                    // Sem triagem: distinguir "ainda processando" de "sem dados"
                                    // pela idade da última mensagem. Salas recentes (< 24h)
                                    // ainda têm chance de ser triadas pelo agente.
                                    const dt = sala.lastMessageDate || sala.updatedAt;
                                    const horas = dt ? (Date.now() - new Date(dt)) / 3600000 : Infinity;
                                    return horas < 24
                                      ? <span style={{ color: '#a855f7', fontSize: '0.72rem', fontWeight: 600 }} title="Aguardando o agente IA classificar">⏳ Triando</span>
                                      : <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }} title="Sem triagem disponível">—</span>;
                                  })()
                              }
                            </td>
                            <td><StatusBadge status={sala.status} /></td>
                            <td className={styles.naoLidasCell}>
                              {naoLidas > 0 ? <span className={styles.badgeNaoLidas}>{naoLidas}</span> : <span className={styles.naoLidasZero}>—</span>}
                            </td>
                            {/* Iniciada = 1ª mensagem da conversa (criado_em/backfill da autocura) */}
                            <td className={styles.dataCell}>
                              {sala.createdAt
                                ? new Date(sala.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                                : '—'}
                            </td>
                            <td className={styles.dataCell} style={{ paddingRight: 20 }}>{formatarHorario(sala.lastMessageDate || sala.updatedAt)}</td>
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>
          )}


          {totalPaginas > 1 && (
            <div className={styles.paginacao}>
              <button id="octadesk-prev-page" className={styles.pgBtn} disabled={page === 1} onClick={() => handlePagina(page - 1)}>‹ Anterior</button>
              {Array.from({ length: Math.min(totalPaginas, 7) }, (_, i) => {
                let pg = totalPaginas <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPaginas - 3 ? totalPaginas - 6 + i : page - 3 + i;
                return (
                  <button key={pg} id={`octadesk-page-${pg}`}
                    className={`${styles.pgBtn} ${pg === page ? styles.pgAtivo : ''}`}
                    onClick={() => handlePagina(pg)}>{pg}</button>
                );
              })}
              <button id="octadesk-next-page" className={styles.pgBtn} disabled={page === totalPaginas} onClick={() => handlePagina(page + 1)}>Próxima ›</button>
            </div>
          )}
        </section>
        </>
      )}


      {/* Modal dos cards — fora das abas de proposito: abre sobre qualquer uma delas */}
      <ModalCard
        dados={modalCard}
        pagina={paginaPainel}
        porPagina={POR_PAGINA_PAINEL}
        onPagina={setPaginaPainel}
        onFechar={fecharModal}
        onAbrirConversa={(r) => { setModalCard(null); setSalaSelecionada(r); }}
        triagens={triagens}
      />

      {/* Exportação em .txt — filtros próprios, abre com os da tela */}
      <ModalExportarConversas
        aberto={modalExport}
        padrao={padraoExport}
        agentes={metricas?.rankingAgentes || []}
        onFechar={() => setModalExport(false)}
      />

      {salaSelecionada && (
        <PainelConversa
          sala={salaSelecionada}
          triagem={triagens[salaSelecionada.key || salaSelecionada._id] || null}
          onFechar={() => setSalaSelecionada(null)}
          filtroProduto={filtroProduto}
        />
      )}

      {/* Modal de conversas por sub-categoria REMOVIDO em 2026-08-24 junto com o painel
          "Reclamações por Motivo" que o abria — sem ele o modal virava código morto
          (só restava o setModalRecl(null) de fechar, nada que abrisse).
          O drill-down equivalente hoje é o painel dos 4 cards de grupo. */}
    </div>
  );
}
